import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings, invalidateTenantSettings } from '../hooks/useTenantSettings';
import { formatDate, formatDateTime } from '../utils/dateFormatting';
import { useTier } from '../context/TierContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { TimeField } from '../components/ui/time-field';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Settings as SettingsIcon,
  Building2,
  Clock,
  Save,
  Globe,
  Users as UsersIcon,
  Search,
  Trash2,
  Shield,
  UserCog,
  User,
  MoreHorizontal,
  DollarSign,
  Percent,
  Brain,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  CheckCircle2,
  Power,
  CreditCard,
  Zap,
  TrendingUp,
  Check,
  Plus,
  Minus,
  ArrowDown,
  ArrowUpCircle,
  Crown,
  Loader2,
  Lock,
  HelpCircle,
  Edit,
  KeyRound,
  UserX,
  UserCheck,
  History,
  Copy,
  AlertCircle,
  Calculator,
  Link,
  Unlink,
  Gift,
  HardDrive,
  AlertTriangle,
  Ban,
  Calendar,
  SlidersHorizontal,
  Mail,
  Bell,
  Receipt,
  Info,
  Truck,
  Users,
  Activity,
  XCircle,
  Upload,
  ImageIcon,
  MapPin,
  Gauge,
  FileText,
  Camera,
  RefreshCw,
  Navigation,
  Webhook,
  Hash,
  PenLine,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { SUPPORTED_LOCALES } from '../hooks/useLocale';
import SupportTab from '../components/SupportTab';
import NotificationsSettingsTab from '../components/NotificationsSettingsTab';
import DocketLineRulesSettings from '../components/DocketLineRulesSettings';
import DocketPdfTemplateSettings from '../components/DocketPdfTemplateSettings';
import PPDCreditsCard from '../components/PPDCreditsCard';
import PayrollEmployeeMapping from '../components/PayrollEmployeeMapping';
import PayrollRateMapping from '../components/PayrollRateMapping';
import TrackingRulesManager from '../components/TrackingRulesManager';
import PayrollRulesManager from '../components/PayrollRulesManager';
import PublicHolidaysManager from '../components/PublicHolidaysManager';
import AWPIntegrationSettings from '../components/AWPIntegrationSettings';
import { COUNTRIES } from '../utils/countryStates';
import { hasPermission, isAdminUser, getUserRoles, ROLE_LABELS, ROLE_DESCRIPTIONS, ROLE_COLORS, ASSIGNABLE_ROLES } from '../lib/permissions';

// Currency options
const CURRENCIES = [
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'NZD', symbol: '$', name: 'New Zealand Dollar' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: '$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: '$', name: 'Hong Kong Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
];

// Common tax types
const TAX_TYPES = [
  { name: 'GST', description: 'Goods and Services Tax' },
  { name: 'VAT', description: 'Value Added Tax' },
  { name: 'Sales Tax', description: 'Sales Tax' },
  { name: 'HST', description: 'Harmonized Sales Tax' },
  { name: 'None', description: 'No Tax' },
];

// Common timezones grouped by region
const TIMEZONES = [
  { group: 'Australia', zones: [
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  ]},
  { group: 'New Zealand', zones: [
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands' },
  ]},
  { group: 'Asia', zones: [
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  ]},
  { group: 'Europe', zones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  ]},
  { group: 'Americas', zones: [
    { value: 'America/New_York', label: 'New York (EST/EDT)' },
    { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  ]},
  { group: 'UTC', zones: [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  ]},
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY (31-Dec-2025)' },
];

import { useSearchParams, Link as RouterLink } from 'react-router-dom';

// Inline component: renders the result of a "Test Connection" live probe
// against an accounting platform (Xero / MYOB / QuickBooks).
const TestConnectionResult = ({ result }) => {
  const { fmtDateTime } = useTenantSettings();
  const [showTechnical, setShowTechnical] = useState(false);
  if (!result) return null;

  if (result.ok) {
    return (
      <div
        className="mt-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
        data-testid={`test-connection-result-${result.platform}`}
      >
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-green-800 dark:text-green-200">
              Connection healthy · {result.latency_ms}ms
            </p>
            <div className="text-xs text-green-700 dark:text-green-300 mt-1 space-y-0.5">
              {result.org_name && <p>Organisation: <span className="font-mono">{result.org_name}</span></p>}
              {result.company_name && <p>Company: <span className="font-mono">{result.company_name}</span></p>}
              {result.environment && <p>Environment: <span className="font-mono">{result.environment}</span></p>}
              {result.token_expires_at && (
                <p>Token expires: <span className="font-mono">{fmtDateTime(result.token_expires_at)}</span></p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
      data-testid={`test-connection-result-${result.platform}`}
    >
      <div className="flex items-start gap-2">
        <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-medium text-red-800 dark:text-red-200">{result.message || 'Connection failed'}</p>
          {result.detail && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">{result.detail}</p>
          )}
          {result.suggested_fix && (
            <p className="text-xs mt-2 text-red-700 dark:text-red-300 border-l-2 border-red-400 pl-2">
              <span className="font-semibold">Suggested fix:</span> {result.suggested_fix}
            </p>
          )}
          {result.technical && (
            <div className="mt-2">
              <button
                type="button"
                className="text-xs underline text-red-700 dark:text-red-300"
                onClick={() => setShowTechnical((v) => !v)}
              >
                {showTechnical ? 'Hide technical details' : 'Show technical details'}
              </button>
              {showTechnical && (
                <pre className="mt-1 p-2 rounded bg-red-100 dark:bg-red-950/60 text-[11px] text-red-900 dark:text-red-200 whitespace-pre-wrap break-all max-h-48 overflow-auto">
                  {result.technical}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline component: a "Refresh Accounts" button + last-sync summary used in
// the Accounting tab for each connected platform. Click → triggers a sync of
// MYOB Items / Xero Accounts / QuickBooks Items into the local cache so the
// next invoice export can pre-flight validate Account IDs against real data.
const AccountSyncRow = ({ platform, state, onSync, dataTestId, connectionId }) => {
  const { api } = useAuth();
  const { fmtDateTime } = useTenantSettings();
  const label = { myob: 'MYOB', xero: 'Xero', quickbooks: 'QuickBooks' }[platform] || platform;
  const summary = state?.summary;
  const lastAt = summary?.last_synced_at ? fmtDateTime(summary.last_synced_at) : null;

  const [viewOpen, setViewOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [search, setSearch] = useState('');

  const loadCodes = async () => {
    setLoadingCodes(true);
    try {
      const path = platform === 'quickbooks' ? '/quickbooks/accounts' : `/${platform}/accounts`;
      const params = connectionId ? { connection_id: connectionId } : {};
      const r = await api.get(path, { params });
      setCodes(r.data.items || []);
    } catch (e) {
      setCodes([]);
    } finally {
      setLoadingCodes(false);
    }
  };

  const openView = () => { setViewOpen(true); loadCodes(); };

  const typeLabel = (t) => ({ item: 'Item', income_account: 'Account', account: 'Account' }[t] || t || '—');
  const filtered = codes.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.code || '').toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q);
  });

  return (
    <div
      className="border rounded-lg p-3 bg-muted/30 flex items-center justify-between gap-3"
      data-testid={dataTestId || `account-sync-row-${platform}`}
    >
      <div className="text-sm">
        <div className="font-medium flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          {label} Account & Item Cache
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {summary?.count ? (
            <>{summary.count} items cached{lastAt ? ` · last synced ${lastAt}` : ''}</>
          ) : (
            <>Not yet synced — pricelist Account IDs can't be validated until you click Refresh.</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {summary?.count ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={openView}
            data-testid={`view-accounts-${platform}-btn`}
          >
            View codes
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSync(platform, connectionId)}
          disabled={state?.loading}
          data-testid={`refresh-accounts-${platform}-btn`}
        >
          {state?.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl" data-testid={`accounts-dialog-${platform}`}>
          <DialogHeader>
            <DialogTitle>{label} — Synced Account &amp; Item Codes</DialogTitle>
            <DialogDescription>
              These are the codes EQUIPR pulled from {label}. A pricelist "Account ID" must match a code below.
              {lastAt ? ` Last synced ${lastAt}.` : ''}
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Search by code or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2"
            data-testid={`accounts-search-${platform}`}
          />
          <div className="max-h-[55vh] overflow-y-auto border rounded-md">
            {loadingCodes ? (
              <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin inline" />Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No codes {search ? 'match your search' : 'cached yet'}.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c, i) => (
                    <TableRow key={`${c.code}-${i}`} data-testid={`account-code-row-${platform}-${i}`}>
                      <TableCell className="font-mono text-xs">{c.code}</TableCell>
                      <TableCell className="text-sm">{c.name || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{typeLabel(c.type)}</Badge></TableCell>
                      <TableCell>
                        {c.is_active === false
                          ? <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-400">Inactive</Badge>
                          : <Badge variant="outline" className="text-[10px] text-green-600 border-green-400">Active</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} of {codes.length} codes</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tags available for the invoice reference template (mirrors backend
// services/invoice_reference.py REFERENCE_TAGS).
const REFERENCE_TAGS = [
  { tag: '{po_number}', label: 'PO Number' },
  { tag: '{job_number}', label: 'Job Number' },
  { tag: '{docket_number}', label: 'Docket Number' },
  { tag: '{job_description}', label: 'Job Description' },
  { tag: '{customer_name}', label: 'Customer Name' },
  { tag: '{job_address}', label: 'Job Address' },
  { tag: '{site_suburb}', label: 'Site Suburb' },
  { tag: '{site_state}', label: 'Site State' },
  { tag: '{job_date}', label: 'Job Date' },
];

// Sample values for the live preview.
const REFERENCE_SAMPLE = {
  po_number: 'PO-4821',
  job_number: '57',
  docket_number: '57-2',
  job_description: 'Excavator hire',
  customer_name: 'Acme Constructions',
  job_address: '12 Smith St, Roma, QLD 4455',
  site_suburb: 'Roma',
  site_state: 'QLD',
  job_date: '2026-06-15',
};

// Client-side mirror of backend render_reference() for the live preview.
const renderReferencePreview = (template, ctx = REFERENCE_SAMPLE) => {
  if (!template) return '';
  let out = template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = ctx[key];
    return (val === undefined || val === null || val === '') ? '' : String(val);
  });
  out = out.replace(/\s{2,}/g, ' ');
  out = out.replace(/(?:\s*[-–—|/]\s*){2,}/g, ' - ');
  out = out.replace(/^[\s\-–—|/,]+|[\s\-–—|/,]+$/g, '');
  return out.trim();
};


const Settings = () => {
  const { api, user, refreshUser, subscriptionStatus, trialEndsAt } = useAuth();
  const isSuspended = subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled';
  const { hasFeature, hasTierFeature } = useTier();
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const refreshAttemptedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL for tab parameter
    const tabParam = searchParams.get('tab');
    const validTabs = isSuspended
      ? ['billing']
      : ['organization', 'users', 'billing', 'ai', 'accounting', 'timesheets', 'account', 'support', 'rules', 'notifications'];
    return validTabs.includes(tabParam) ? tabParam : (isSuspended ? 'billing' : 'organization');
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [testingApiKey, setTestingApiKey] = useState(false);
  const [apiKeyValid, setApiKeyValid] = useState(null);
  const [form, setForm] = useState({
    client_id: '',
    company_name: '',
    legal_entity_name: '',
    business_number: '',
    business_address: '',
    phone_number: '',
    industry: 'crane_hire',
    country: 'AU',
    timezone: 'Australia/Sydney',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    default_working_hours: {
      start: '07:00',
      end: '17:00'
    },
    depot_time_enabled: false,
    default_depot_time: '06:30',
    depots_enabled: false,
    worker_job_email_enabled: false,
    locale: 'en-AU',
    field_rules: {
      depot_time: { enabled: false, required: false },
      additional_workers: { enabled: true, required: false },
      asset_allocation_on_create: { enabled: false, required: false },
      smu: {
        enabled: false,
        secondary_meter_enabled: false,
        invoice_calculation_mode: 'prefer_smu_fall_back_to_time',
        required_to_complete_job: false,
      },
    },
    currency: 'AUD',
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    include_photos_in_docket_pdf: false,
    auto_email_docket_on_submit: false,
    auto_email_recipient_groups: ['customer', 'site_contacts', 'creator'],
    ai_enabled: false,
    openai_api_key: '',
    pay_cycle_type: 'weekly',
    pay_cycle_day: 1,
    pay_cycle_start_date: '',
    payroll_calc_method: 'actual_hours',
    overtime_threshold_hours: 8,
    overtime_1_5_threshold_hours: 10,
    overtime_method: 'disabled',
    overtime_threshold_hours: 8,
    travel_method: 'disabled',
    minimum_charge_enabled: false,
    fuel_levy_enabled: false,
    min_hire_includes_travel: false,
    fuel_levy_percentage: 0,
    fuel_levy_basis: 'invoice_total',
    fuel_levy_account_id: '',
    skip_docket_approval: false,
    separate_operator_line: false,
    docket_depot_times_enabled: false,
    auto_confirm_jobs: true,
    cancellation_reasons: [],
    docket_signature_link_expiry_hours: 24,
    haulage_signature_timing: 'end',
    haulage_waypoint_signature: true
  });

  // My Account / Password Change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Company Logo state
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  // User Management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', full_name: '', roles: ['dispatcher'] });
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [tempPasswordModal, setTempPasswordModal] = useState({ open: false, password: '', username: '' });
  const [activityModal, setActivityModal] = useState({ open: false, user: null, logs: [] });
  const [activityLoading, setActivityLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, user: null });
  const [crossTypeDialog, setCrossTypeDialog] = useState({ open: false, match: null });
  const [linkedOperatorDialog, setLinkedOperatorDialog] = useState({ open: false, worker: null, message: '', confirmCrossType: false });
  const [editOperatorModal, setEditOperatorModal] = useState({ open: false, operator: null });
  const [editOperatorForm, setEditOperatorForm] = useState({ name: '', email: '' });
  const [editOperatorLoading, setEditOperatorLoading] = useState(false);
  const [operatorCrossTypeDialog, setOperatorCrossTypeDialog] = useState({ open: false, match: null, message: '' });
  
  // Billing state
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [allTiers, setAllTiers] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Subscription cancellation state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [extraResourceQuantity, setExtraResourceQuantity] = useState(1);
  
  // Storage state
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [isDemoTenant, setIsDemoTenant] = useState(false);

  // Compliance Digest state (Professional)
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState('weekly');
  const [digestRecipients, setDigestRecipients] = useState([]);
  const [savingDigest, setSavingDigest] = useState(false);

  // Xero/Accounting state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: null, connected_at: null });
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroConnecting, setXeroConnecting] = useState(false);
  const [xeroImporting, setXeroImporting] = useState(false);
  const [xeroImportResult, setXeroImportResult] = useState(null);
  const [invoiceNumberingSource, setInvoiceNumberingSource] = useState('accounting_package');
  const [xeroLineCodeMode, setXeroLineCodeMode] = useState('account');
  const [savingXeroLineCodeMode, setSavingXeroLineCodeMode] = useState(false);
  const [myobLineCodeMode, setMyobLineCodeMode] = useState('item');
  const [savingMyobLineCodeMode, setSavingMyobLineCodeMode] = useState(false);
  const [connectedAccountingPackage, setConnectedAccountingPackage] = useState(null);
  const [acctConnections, setAcctConnections] = useState([]);
  const [renamingConnId, setRenamingConnId] = useState(null);
  const [renameLabel, setRenameLabel] = useState('');
  const [savingInvoiceSetting, setSavingInvoiceSetting] = useState(false);

  // Test Connection results - keyed by platform
  const [testingConnection, setTestingConnection] = useState({ xero: false, myob: false, quickbooks: false });
  const [testConnectionResult, setTestConnectionResult] = useState({ xero: null, myob: null, quickbooks: null });

  // Export failures list
  const [exportFailures, setExportFailures] = useState([]);
  const [loadingFailures, setLoadingFailures] = useState(false);

  
  // Timesheets settings state
  const [savingPayCycle, setSavingPayCycle] = useState(false);
  
  // MYOB state
  const [myobStatus, setMyobStatus] = useState({ connected: false, company_name: null, connected_at: null, needs_reauth: false, reauth_reason: null, product_type: null, has_cf_credentials: false });
  const [myobLoading, setMyobLoading] = useState(false);
  const [myobConnecting, setMyobConnecting] = useState(false);
  const [myobImporting, setMyobImporting] = useState(false);
  const [myobImportResult, setMyobImportResult] = useState(null);
  const [myobSettings, setMyobSettings] = useState({ product_type: null, cf_username: '', cf_password: '' });
  const [myobSettingsSaving, setMyobSettingsSaving] = useState(false);
  const [showMyobSetup, setShowMyobSetup] = useState(false);
  const [showAddMyobDialog, setShowAddMyobDialog] = useState(false);
  const [addMyobSettings, setAddMyobSettings] = useState({ product_type: '', cf_username: '', cf_password: '' });

  // QuickBooks state
  const [qboStatus, setQboStatus] = useState({ connected: false, company_name: null, connected_at: null, realm_id: null });
  const [qboLoading, setQboLoading] = useState(false);
  const [qboConnecting, setQboConnecting] = useState(false);
  const [qboImporting, setQboImporting] = useState(false);
  const [qboImportResult, setQboImportResult] = useState(null);
  const [jobNumbering, setJobNumbering] = useState(null);
  const [numberingInput, setNumberingInput] = useState('');
  const [savingNumbering, setSavingNumbering] = useState(false);
  const [invoiceReferenceTemplate, setInvoiceReferenceTemplate] = useState('');
  const [savingReferenceTemplate, setSavingReferenceTemplate] = useState(false);
  const DEFAULT_INVOICE_EMAIL_SUBJECT = 'Invoice {invoice_number} from {company_name}';
  const DEFAULT_INVOICE_EMAIL_BODY = 'Hi {customer_name},\n\nPlease find attached invoice {invoice_number} dated {invoice_date} for {total_amount}.\n\nKind regards,\n{company_name}';
  const [invoiceEmailSubject, setInvoiceEmailSubject] = useState('');
  const [invoiceEmailBody, setInvoiceEmailBody] = useState('');
  const [savingInvoiceEmail, setSavingInvoiceEmail] = useState(false);

  useEffect(() => {
    if (!user?.client_id) {
      // Guard: client_id not hydrated yet (stale stored user, or
      // AuthContext.initAuth still running). Trigger a one-shot refresh
      // and clear the loading skeleton so the page isn't stuck on a blank
      // shimmer. The effect re-fires automatically when client_id arrives.
      setLoading(false);
      if (user && refreshUser && !refreshAttemptedRef.current) {
        refreshAttemptedRef.current = true;
        refreshUser();
      }
      return;
    }
    const fetchTenant = async () => {
      try {
        const response = await api.get(`/tenants/${user.client_id}`);
        setIsDemoTenant(!!response.data.is_demo);
        setForm({
          client_id: response.data.client_id,
          company_name: response.data.company_name,
          legal_entity_name: response.data.legal_entity_name || '',
          business_number: response.data.business_number || '',
          business_address: response.data.business_address || '',
          phone_number: response.data.phone_number || '',
          industry: response.data.industry || 'crane_hire',
          country: response.data.country || 'AU',
          timezone: response.data.timezone || 'Australia/Sydney',
          date_format: response.data.date_format || 'DD/MM/YYYY',
          time_format: response.data.time_format || '24h',
          default_working_hours: response.data.default_working_hours || { start: '07:00', end: '17:00' },
          depot_time_enabled: response.data.depot_time_enabled || response.data.field_rules?.depot_time?.enabled || false,
          default_depot_time: response.data.default_depot_time || '06:30',
          depots_enabled: response.data.depots_enabled ?? false,
          worker_job_email_enabled: response.data.worker_job_email_enabled ?? false,
          default_worker_notification_preference: response.data.default_worker_notification_preference || 'email',
          locale: response.data.locale || 'en-AU',
          field_rules: {
            depot_time: { enabled: response.data.field_rules?.depot_time?.enabled ?? response.data.depot_time_enabled ?? false, required: false },
            additional_workers: { enabled: response.data.field_rules?.additional_workers?.enabled ?? true, required: false },
            asset_allocation_on_create: { enabled: response.data.field_rules?.asset_allocation_on_create?.enabled ?? false, required: false },
            ...(response.data.field_rules || {}),
          },
          currency: response.data.currency || 'AUD',
          currency_symbol: response.data.currency_symbol || '$',
          tax_name: response.data.tax_name || 'GST',
          tax_rate: response.data.tax_rate ?? 10,
          include_photos_in_docket_pdf: response.data.include_photos_in_docket_pdf ?? false,
          auto_email_docket_on_submit: response.data.auto_email_docket_on_submit ?? false,
          auto_email_recipient_groups: response.data.auto_email_recipient_groups || ['customer', 'site_contacts', 'creator'],
          ai_enabled: response.data.ai_enabled ?? false,
          openai_api_key: response.data.openai_api_key || '',
          pay_cycle_type: response.data.pay_cycle_type || 'weekly',
          pay_cycle_day: response.data.pay_cycle_day ?? 1,
          pay_cycle_start_date: response.data.pay_cycle_start_date || '',
          payroll_calc_method: response.data.payroll_calc_method || 'actual_hours',
          overtime_threshold_hours: response.data.overtime_threshold_hours ?? 8,
          overtime_1_5_threshold_hours: response.data.overtime_1_5_threshold_hours ?? 10,
          overtime_method: response.data.overtime_method || 'disabled',
          overtime_threshold_hours: response.data.overtime_threshold_hours ?? 8,
          travel_method: response.data.travel_method || 'disabled',
          minimum_charge_enabled: response.data.minimum_charge_enabled || false,
          fuel_levy_enabled: response.data.fuel_levy_enabled || false,
          min_hire_includes_travel: response.data.min_hire_includes_travel ?? false,
          fuel_levy_percentage: response.data.fuel_levy_percentage ?? 0,
          fuel_levy_basis: response.data.fuel_levy_basis || 'invoice_total',
          fuel_levy_account_id: response.data.fuel_levy_account_id || '',
          skip_docket_approval: response.data.skip_docket_approval || false,
          separate_operator_line: response.data.separate_operator_line || false,
          docket_depot_times_enabled: response.data.docket_depot_times_enabled || false,
          auto_confirm_jobs: response.data.auto_confirm_jobs ?? true,
          cancellation_reasons: response.data.cancellation_reasons || [],
          docket_signature_link_expiry_hours: response.data.docket_signature_link_expiry_hours ?? 24,
          haulage_signature_timing: response.data.haulage_signature_timing || 'end',
          haulage_waypoint_signature: response.data.haulage_waypoint_signature ?? true,
          relaxed_allocation: response.data.relaxed_allocation ?? false
        });
        // If API key exists, consider it valid (already saved)
        if (response.data.openai_api_key) {
          setApiKeyValid(true);
        }
        // Load logo URL from tenant data
        if (response.data.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
        // Load compliance digest settings
        setDigestEnabled(response.data.compliance_digest_enabled ?? false);
        setDigestFrequency(response.data.compliance_digest_frequency || 'weekly');
        setDigestRecipients(response.data.compliance_digest_recipients || []);
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [api, user, refreshUser]);

  // Load current job/docket numbering (separate from tenant doc — it's a counter)
  const fetchJobNumbering = useCallback(async () => {
    try {
      const res = await api.get('/jobs/numbering');
      setJobNumbering(res.data);
      setNumberingInput(String(res.data.next_number));
    } catch (e) { /* non-fatal */ }
  }, [api]);

  useEffect(() => {
    if (user?.client_id) fetchJobNumbering();
  }, [user?.client_id, fetchJobNumbering]);

  const handleSaveJobNumbering = async () => {
    const val = parseInt(numberingInput, 10);
    if (!Number.isInteger(val) || val < 1) { toast.error('Enter a valid number'); return; }
    if (jobNumbering && val < jobNumbering.min_allowed) {
      toast.error(`Number must be ${jobNumbering.min_allowed} or higher (you've already used up to ${jobNumbering.highest_used}).`);
      return;
    }
    setSavingNumbering(true);
    try {
      const res = await api.put('/jobs/numbering', { next_number: val });
      setJobNumbering(res.data);
      setNumberingInput(String(res.data.next_number));
      toast.success(`Next job & docket number set to ${res.data.next_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update numbering');
    } finally {
      setSavingNumbering(false);
    }
  };

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'organization') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Listen for URL changes (e.g., from browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['organization', 'users', 'billing', 'ai', 'accounting', 'timesheets', 'account', 'support', 'rules', 'notifications'];
    if (validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    
    // Handle Xero OAuth callback parameters
    const xeroSuccess = searchParams.get('xero_success');
    const xeroError = searchParams.get('xero_error');
    
    if (xeroSuccess === 'true') {
      toast.success('Xero connected successfully!');
      fetchXeroStatus();
      fetchMyobStatus();
      // Clean up URL
      searchParams.delete('xero_success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (xeroError) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to connect to Xero. Please try again.',
        'connections_failed': 'Failed to get Xero organizations. Please try again.',
        'no_organizations': 'No Xero organizations found. Please ensure you have access to at least one organization.'
      };
      toast.error(errorMessages[xeroError] || 'Xero connection failed. Please try again.');
      // Clean up URL
      searchParams.delete('xero_error');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Handle MYOB OAuth callback parameters
    const myobSuccess = searchParams.get('myob_success');
    const myobError = searchParams.get('myob_error');
    
    if (myobSuccess === 'true') {
      toast.success('MYOB connected successfully!');
      fetchMyobStatus();
      fetchXeroStatus();
      // Clean up URL
      searchParams.delete('myob_success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (myobError) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to connect to MYOB. Please try again.',
        'company_files_failed': 'Failed to get MYOB company files. Please try again.',
        'no_company_files': 'No MYOB company files found. Please ensure you have access to at least one company file.',
        'invalid_state': 'Invalid session. Please try connecting again.',
        'missing_params': 'Missing authorization parameters. Please try again.'
      };
      toast.error(errorMessages[myobError] || 'MYOB connection failed. Please try again.');
      // Clean up URL
      searchParams.delete('myob_error');
      setSearchParams(searchParams, { replace: true });
    }
    
    // QuickBooks callback handling
    const qboSuccess = searchParams.get('qbo_success');
    const qboError = searchParams.get('qbo_error');
    
    if (qboSuccess === 'true') {
      toast.success('QuickBooks connected successfully!');
      fetchQboStatus();
      fetchXeroStatus();
      fetchMyobStatus();
      searchParams.delete('qbo_success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (qboError) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to connect to QuickBooks. Please try again.',
        'invalid_state': 'Invalid session. Please try connecting again.'
      };
      toast.error(errorMessages[qboError] || 'QuickBooks connection failed. Please try again.');
      searchParams.delete('qbo_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // Fetch Xero connection status and accounting settings
  const fetchXeroStatus = async () => {
    try {
      setXeroLoading(true);
      const response = await api.get('/xero/status');
      setXeroStatus(response.data);
      
      // Also fetch tenant settings for invoice numbering
      const tenantResponse = await api.get(`/tenants/${user.client_id}`);
      setInvoiceNumberingSource(tenantResponse.data.invoice_numbering_source || 'accounting_package');
      setXeroLineCodeMode(tenantResponse.data.xero_line_code_mode || 'account');
      setMyobLineCodeMode(tenantResponse.data.myob_line_code_mode || 'item');
      setConnectedAccountingPackage(tenantResponse.data.connected_accounting_package || null);
      setInvoiceReferenceTemplate(tenantResponse.data.invoice_reference_template || '');
      setInvoiceEmailSubject(tenantResponse.data.invoice_email_subject || DEFAULT_INVOICE_EMAIL_SUBJECT);
      setInvoiceEmailBody(tenantResponse.data.invoice_email_body || DEFAULT_INVOICE_EMAIL_BODY);
      try {
        const connRes = await api.get('/accounting/connections');
        setAcctConnections(connRes.data.connections || []);
      } catch (e) { /* non-fatal */ }
    } catch (error) {
      console.error('Failed to fetch Xero status:', error);
    } finally {
      setXeroLoading(false);
    }
  };
  
  // Multi-connection management handlers
  const fetchAcctConnections = async () => {
    try {
      const res = await api.get('/accounting/connections');
      setAcctConnections(res.data.connections || []);
    } catch (e) { /* non-fatal */ }
  };

  const handleSetDefaultConnection = async (connId) => {
    setAcctConnections((prev) => prev.map((c) => ({ ...c, is_default: c.id === connId })));
    try {
      await api.patch(`/accounting/connections/${connId}`, { is_default: true });
      toast.success('Default accounting connection updated');
      await fetchAcctConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default');
      await fetchAcctConnections();
    }
  };

  const handleRenameConnection = async (connId) => {
    if (!renameLabel.trim()) { setRenamingConnId(null); return; }
    try {
      await api.patch(`/accounting/connections/${connId}`, { label: renameLabel.trim() });
      toast.success('Connection renamed');
      setRenamingConnId(null);
      setRenameLabel('');
      await fetchAcctConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    }
  };

  const handleDisconnectConnection = async (connId) => {
    setAcctConnections((prev) => prev.filter((c) => c.id !== connId));
    try {
      await api.delete(`/accounting/connections/${connId}`);
      toast.success('Connection disconnected');
      await fetchAcctConnections();
      fetchXeroStatus();
      fetchMyobStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
      await fetchAcctConnections();
    }
  };

  // Fetch MYOB connection status
  const fetchMyobStatus = async () => {
    try {
      setMyobLoading(true);
      const response = await api.get('/myob/status');
      setMyobStatus(response.data);
      
      // Also fetch tenant settings
      const tenantResponse = await api.get(`/tenants/${user.client_id}`);
      setInvoiceNumberingSource(tenantResponse.data.invoice_numbering_source || 'accounting_package');
      setXeroLineCodeMode(tenantResponse.data.xero_line_code_mode || 'account');
      setMyobLineCodeMode(tenantResponse.data.myob_line_code_mode || 'item');
      setConnectedAccountingPackage(tenantResponse.data.connected_accounting_package || null);
      setInvoiceReferenceTemplate(tenantResponse.data.invoice_reference_template || '');
    } catch (error) {
      console.error('Failed to fetch MYOB status:', error);
    } finally {
      setMyobLoading(false);
    }
  };

  // Save invoice numbering setting
  const handleSaveInvoiceNumberingSetting = async (value) => {
    try {
      setSavingInvoiceSetting(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        invoice_numbering_source: value
      });
      setInvoiceNumberingSource(value);
      toast.success('Invoice numbering setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSavingInvoiceSetting(false);
    }
  };

  // Save Xero line item code mode (Account Code vs Item Code for Allocate migrants)
  const handleSaveXeroLineCodeMode = async (value) => {
    try {
      setSavingXeroLineCodeMode(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        xero_line_code_mode: value
      });
      setXeroLineCodeMode(value);
      toast.success('Xero line item code setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSavingXeroLineCodeMode(false);
    }
  };

  const handleSaveMyobLineCodeMode = async (value) => {
    try {
      setSavingMyobLineCodeMode(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        myob_line_code_mode: value
      });
      setMyobLineCodeMode(value);
      toast.success('MYOB line code setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSavingMyobLineCodeMode(false);
    }
  };

  // Save invoice reference template
  const handleSaveReferenceTemplate = async () => {
    try {
      setSavingReferenceTemplate(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        invoice_reference_template: invoiceReferenceTemplate
      });
      toast.success('Invoice reference template saved');
    } catch (error) {
      toast.error('Failed to save reference template');
    } finally {
      setSavingReferenceTemplate(false);
    }
  };

  // Save invoice email template (subject + body)
  const handleSaveInvoiceEmail = async () => {
    try {
      setSavingInvoiceEmail(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        invoice_email_subject: invoiceEmailSubject,
        invoice_email_body: invoiceEmailBody,
      });
      toast.success('Invoice email template saved');
    } catch (error) {
      toast.error('Failed to save invoice email template');
    } finally {
      setSavingInvoiceEmail(false);
    }
  };

  const handleResetInvoiceEmail = () => {
    setInvoiceEmailSubject(DEFAULT_INVOICE_EMAIL_SUBJECT);
    setInvoiceEmailBody(DEFAULT_INVOICE_EMAIL_BODY);
  };

  // Connect to Xero
  const handleConnectXero = async () => {
    try {
      setXeroConnecting(true);
      const response = await api.get('/xero/connect');
      // Redirect to Xero authorization
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Xero connection');
      setXeroConnecting(false);
    }
  };

  // Disconnect Xero
  const handleDisconnectXero = async () => {
    try {
      await api.post('/xero/disconnect');
      setXeroStatus({ connected: false, tenant_name: null, connected_at: null });
      setConnectedAccountingPackage(null);
      toast.success('Xero disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect Xero');
    }
  };

  // Import customers from Xero
  const handleImportCustomersFromXero = async (connectionId) => {
    const key = connectionId || 'default';
    const toastId = toast.loading('Importing customers from Xero — this may take a moment...');
    try {
      setImportingConn((s) => ({ ...s, [`xero:${key}`]: true }));
      setImportResults((s) => ({ ...s, [`xero:${key}`]: null }));
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await api.post('/xero/import-customers', null, { params });
      setImportResults((s) => ({ ...s, [`xero:${key}`]: response.data }));
      setXeroImportResult(response.data);
      const removedMsg = response.data.archived ? `, archived ${response.data.archived} inactive` : '';
      toast.success(`Imported ${response.data.imported} customers, linked ${response.data.linked} existing${removedMsg}`, { id: toastId });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import customers from Xero', { id: toastId });
    } finally {
      setImportingConn((s) => ({ ...s, [`xero:${key}`]: false }));
      setXeroImporting(false);
    }
  };

  // Test live connection to an accounting platform (xero | myob | quickbooks)
  const handleTestAccountingConnection = async (platform) => {
    setTestingConnection(prev => ({ ...prev, [platform]: true }));
    setTestConnectionResult(prev => ({ ...prev, [platform]: null }));
    try {
      const endpoint = platform === 'quickbooks' ? '/quickbooks/test-connection' : `/${platform}/test-connection`;
      const response = await api.get(endpoint);
      setTestConnectionResult(prev => ({ ...prev, [platform]: response.data }));
      if (response.data.ok) {
        toast.success(`${platform === 'quickbooks' ? 'QuickBooks' : platform.toUpperCase()} connection healthy (${response.data.latency_ms}ms)`);
      } else {
        toast.error(response.data.message || `${platform} connection failed`);
      }
    } catch (error) {
      const errRes = {
        ok: false,
        message: error.response?.data?.detail || `Failed to test ${platform} connection`,
        reason: 'request_error',
      };
      setTestConnectionResult(prev => ({ ...prev, [platform]: errRes }));
      toast.error(errRes.message);
    } finally {
      setTestingConnection(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Fetch recent export failures across all accounting platforms
  const fetchExportFailures = async () => {
    setLoadingFailures(true);
    try {
      const response = await api.get('/accounting/export-logs?status=failed&limit=50');
      setExportFailures(response.data.logs || []);
    } catch (error) {
      // Silently fail — this is a secondary view
      setExportFailures([]);
    } finally {
      setLoadingFailures(false);
    }
  };

  
  // Connect to MYOB
  const handleConnectMyob = async () => {
    try {
      setMyobConnecting(true);
      const response = await api.get('/myob/connect');
      // Redirect to MYOB authorization
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate MYOB connection');
      setMyobConnecting(false);
    }
  };

  // Disconnect MYOB
  const handleDisconnectMyob = async () => {
    try {
      await api.post('/myob/disconnect');
      setMyobStatus({ connected: false, company_name: null, connected_at: null });
      setConnectedAccountingPackage(null);
      toast.success('MYOB disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect MYOB');
    }
  };

  // Import customers from MYOB
  // Per-connection import results (keyed by connectionId, 'default' for legacy calls)
  const [importResults, setImportResults] = useState({});
  const [importingConn, setImportingConn] = useState({});

  const handleImportCustomersFromMyob = async (connectionId) => {
    const key = connectionId || 'default';
    const toastId = toast.loading('Importing customers from MYOB — this may take a moment...');
    try {
      setImportingConn((s) => ({ ...s, [key]: true }));
      setImportResults((s) => ({ ...s, [`myob:${key}`]: null }));
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await api.post('/myob/import-customers', null, { params });
      setImportResults((s) => ({ ...s, [`myob:${key}`]: response.data }));
      setMyobImportResult(response.data);
      const myobRemovedMsg = response.data.archived ? `, archived ${response.data.archived} inactive` : '';
      toast.success(`Imported ${response.data.imported} customers, updated ${response.data.updated} existing${myobRemovedMsg}`, { id: toastId });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to import customers from MYOB';
      toast.error(errorDetail, { id: toastId });
      
      if (errorDetail.toLowerCase().includes('authentication expired') || 
          errorDetail.toLowerCase().includes('reconnect')) {
        fetchMyobStatus();
      }
    } finally {
      setImportingConn((s) => ({ ...s, [key]: false }));
      setMyobImporting(false);
    }
  };

  // ---------------------------------------------------------------------
  // Accounting account/item cache sync — shared logic for MYOB, Xero, QBO.
  // Refreshes the local cache of Items + Income Accounts so we can
  // pre-flight validate pricelist Account IDs before pushing invoices.
  // Cache is consulted by the backend at export time.
  // ---------------------------------------------------------------------
  const [accountSyncState, setAccountSyncState] = useState({
    myob: { loading: false, summary: null },
    xero: { loading: false, summary: null },
    quickbooks: { loading: false, summary: null },
  });

  const fetchAccountSyncSummary = async (platform, connectionId) => {
    const key = _syncKey(platform, connectionId);
    try {
      const path = platform === 'quickbooks' ? '/quickbooks/accounts' : `/${platform}/accounts`;
      const params = connectionId ? { connection_id: connectionId } : {};
      const r = await api.get(path, { params });
      setAccountSyncState((s) => ({
        ...s,
        [key]: { ...(s[key] || {}), summary: { count: r.data.count, last_synced_at: r.data.last_synced_at } },
      }));
    } catch (_) {
      // Silent — summary is best-effort. The endpoint requires the platform
      // to be connected; ignore if not.
    }
  };

  // Helper: state key for per-connection sync state
  const _syncKey = (platform, connectionId) => connectionId ? `${platform}:${connectionId}` : platform;

  const handleSyncAccounts = async (platform, connectionId) => {
    const labels = { myob: 'MYOB', xero: 'Xero', quickbooks: 'QuickBooks' };
    const key = _syncKey(platform, connectionId);
    setAccountSyncState((s) => ({ ...s, [key]: { ...(s[key] || {}), loading: true } }));
    try {
      const path = platform === 'quickbooks' ? '/quickbooks/sync-accounts' : `/${platform}/sync-accounts`;
      const params = connectionId ? { connection_id: connectionId } : {};
      const r = await api.post(path, null, { params });
      toast.success(`Synced ${r.data.synced} ${labels[platform]} accounts/items`);
      setAccountSyncState((s) => ({
        ...s,
        [key]: { loading: false, summary: { count: r.data.count, last_synced_at: r.data.last_synced_at } },
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to refresh ${labels[platform]} accounts`);
      setAccountSyncState((s) => ({ ...s, [key]: { ...(s[key] || {}), loading: false } }));
    }
  };

  useEffect(() => {
    // Best-effort summary loads when the relevant integration is connected.
    // Load per-connection summaries for multi-accounting
    if (acctConnections.length > 0) {
      acctConnections.forEach(conn => {
        fetchAccountSyncSummary(conn.provider, conn.id);
      });
    } else {
      // Fallback to legacy single-connection summaries
      if (myobStatus.connected) fetchAccountSyncSummary('myob');
      if (xeroStatus.connected) fetchAccountSyncSummary('xero');
      if (qboStatus.connected) fetchAccountSyncSummary('quickbooks');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myobStatus.connected, xeroStatus.connected, qboStatus.connected, acctConnections.length]);

  // Fetch MYOB settings
  const fetchMyobSettings = async () => {
    try {
      const response = await api.get('/myob/settings');
      setMyobSettings({
        product_type: response.data.product_type,
        cf_username: response.data.cf_username || '',
        cf_password: ''  // Never returned from API for security
      });
    } catch (error) {
      console.error('Failed to fetch MYOB settings:', error);
    }
  };

  // Save MYOB settings and initiate connection
  const handleSaveMyobSettingsAndConnect = async () => {
    if (!myobSettings.product_type) {
      toast.error('Please select your MYOB product type');
      return;
    }
    
    if (myobSettings.product_type === 'accountright' && (!myobSettings.cf_username || !myobSettings.cf_password)) {
      toast.error('Please enter your company file username and password');
      return;
    }
    
    try {
      setMyobSettingsSaving(true);
      
      // Save settings first
      await api.post('/myob/settings', {
        product_type: myobSettings.product_type,
        cf_username: myobSettings.product_type === 'accountright' ? myobSettings.cf_username : null,
        cf_password: myobSettings.product_type === 'accountright' ? myobSettings.cf_password : null
      });
      
      // Then initiate OAuth connection
      const response = await api.get('/myob/connect');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MYOB settings');
      setMyobSettingsSaving(false);
    }
  };

  // Start MYOB setup flow
  const handleStartMyobSetup = () => {
    fetchMyobSettings();
    setShowMyobSetup(true);
  };

  // Connect Another MYOB — opens setup dialog first
  const handleAddMyobConnection = async () => {
    if (!addMyobSettings.product_type) {
      toast.error('Please select your MYOB product type');
      return;
    }
    if (addMyobSettings.product_type === 'accountright' && (!addMyobSettings.cf_username || !addMyobSettings.cf_password)) {
      toast.error('Please enter company file credentials for AccountRight');
      return;
    }
    try {
      setMyobConnecting(true);
      // Save settings (these get picked up by the OAuth callback when creating the connection row)
      await api.post('/myob/settings', {
        product_type: addMyobSettings.product_type,
        cf_username: addMyobSettings.product_type === 'accountright' ? addMyobSettings.cf_username : null,
        cf_password: addMyobSettings.product_type === 'accountright' ? addMyobSettings.cf_password : null
      });
      // Initiate OAuth
      const response = await api.get('/myob/connect');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate MYOB connection');
      setMyobConnecting(false);
    }
  };

  // Fetch Xero and MYOB and QuickBooks status when accounting tab is active
  useEffect(() => {
    if (activeTab === 'accounting') {
      fetchXeroStatus();
      fetchMyobStatus();
      fetchQboStatus();
      fetchExportFailures();
    }
  }, [activeTab]);

  // QuickBooks handlers
  const fetchQboStatus = async () => {
    try {
      setQboLoading(true);
      const response = await api.get('/quickbooks/status');
      setQboStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch QBO status:', error);
    } finally {
      setQboLoading(false);
    }
  };

  const handleConnectQbo = async () => {
    try {
      setQboConnecting(true);
      const response = await api.get('/quickbooks/connect');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate QuickBooks connection');
      setQboConnecting(false);
    }
  };

  const handleDisconnectQbo = async () => {
    try {
      await api.post('/quickbooks/disconnect');
      setQboStatus({ connected: false, company_name: null, connected_at: null, realm_id: null });
      setConnectedAccountingPackage(null);
      toast.success('QuickBooks disconnected successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect QuickBooks');
    }
  };

  const handleImportCustomersFromQbo = async (connectionId) => {
    const key = connectionId || 'default';
    const toastId = toast.loading('Importing customers from QuickBooks — this may take a moment...');
    try {
      setQboImporting(true);
      setImportingConn((s) => ({ ...s, [`quickbooks:${key}`]: true }));
      setImportResults((s) => ({ ...s, [`quickbooks:${key}`]: null }));
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await api.post('/quickbooks/import-customers', null, { params });
      setImportResults((s) => ({ ...s, [`quickbooks:${key}`]: response.data }));
      setQboImportResult(response.data);
      const qboRemovedMsg = response.data.archived ? `, archived ${response.data.archived} inactive` : '';
      toast.success(`Imported ${response.data.imported} customers, linked ${response.data.linked} existing${qboRemovedMsg}`, { id: toastId });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import customers from QuickBooks', { id: toastId });
    } finally {
      setImportingConn((s) => ({ ...s, [`quickbooks:${key}`]: false }));
      setQboImporting(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      // 403 is expected for non-admin users
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch users');
      }
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'ai') {
      fetchUsers();
      // Also fetch billing data to check user limits
      if (activeTab === 'users' && !billingData) {
        fetchBillingData();
      }
    }
  }, [activeTab, api]);

  // Billing data fetch
  const fetchBillingData = async () => {
    setBillingLoading(true);
    setStorageLoading(true);
    setBillingError(false);
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      // (e.g., non-admin users can't access /billing/transactions)
      const [usageRes, tiersRes, transactionsRes, storageRes] = await Promise.allSettled([
        api.get('/billing/usage'),
        api.get('/billing/tiers'),
        api.get('/billing/transactions'),
        api.get('/storage/usage')
      ]);
      
      // Set billing data if usage call succeeded
      if (usageRes.status === 'fulfilled') {
        setBillingData(usageRes.value.data);
      } else {
        console.error('Failed to fetch billing usage:', usageRes.reason);
        setBillingError(true);
        toast.error('Failed to load billing data');
      }
      
      // Set tiers if call succeeded
      if (tiersRes.status === 'fulfilled') {
        setAllTiers(tiersRes.value.data.tiers);
      }
      
      // Set transactions if call succeeded (may fail for non-admin users)
      if (transactionsRes.status === 'fulfilled') {
        setTransactions(transactionsRes.value.data);
      }
      // Don't show error for transactions - it's expected to fail for non-admins
      
      // Set storage data if call succeeded
      if (storageRes.status === 'fulfilled') {
        setStorageData(storageRes.value.data);
      }
    } catch (error) {
      setBillingError(true);
      toast.error('Failed to load billing data');
    } finally {
      setBillingLoading(false);
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'billing') {
      fetchBillingData();
    }
  }, [activeTab, api]);

  // Check for session_id in URL (returning from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const tab = urlParams.get('tab');
    const cancelled = urlParams.get('cancelled');
    
    if (tab === 'billing') {
      setActiveTab('billing');
    }
    
    if (sessionId) {
      // Poll for payment status
      const checkPaymentStatus = async () => {
        try {
          const response = await api.get(`/billing/checkout/status/${sessionId}`);
          if (response.data.status === 'paid') {
            toast.success('Payment successful! Your account has been updated.');
            fetchBillingData();
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      };
      checkPaymentStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (cancelled) {
      toast.info('Payment cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Checkout handlers
  const handleSubscriptionUpgrade = async (tier) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/checkout/subscription', {
        tier: tier,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleChangePlan = async (newTier) => {
    // Determine if this is an upgrade or downgrade
    const tierOrder = { basic: 0, advanced: 1, professional: 2 };
    const currentTierLevel = tierOrder[billingData?.tier] || 0;
    const newTierLevel = tierOrder[newTier] || 0;
    const isUpgrade = newTierLevel > currentTierLevel;
    
    const tierName = newTier.charAt(0).toUpperCase() + newTier.slice(1);
    
    let confirmMessage;
    if (isUpgrade) {
      confirmMessage = `Upgrade to ${tierName} plan?\n\n• New features will be available immediately\n• You'll be charged the prorated difference for this billing period\n• Extra operators and users will be migrated to new pricing`;
    } else {
      confirmMessage = `Downgrade to ${tierName} plan?\n\n• You'll keep your current ${billingData?.tier_name || 'plan'} features until the end of your billing period\n• The new plan will take effect at your next billing cycle\n• Extra operators and users will be migrated to new pricing`;
    }
    
    setConfirmDialog({
      open: true,
      title: isUpgrade ? 'Upgrade Plan' : 'Downgrade Plan',
      description: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setCheckoutLoading(true);
        try {
          const response = await api.post('/billing/change-plan', { tier: newTier });
          if (response.data.change_type === 'upgrade') {
            toast.success(`Upgraded to ${tierName}! Prorated charges have been applied.`);
          } else {
            toast.success(`Downgrade scheduled. You'll switch to ${tierName} on ${response.data.effective_date || 'your next billing cycle'}.`);
          }
          const billingRes = await api.get('/billing/usage');
          setBillingData(billingRes.data);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to change plan');
        } finally {
          setCheckoutLoading(false);
        }
      },
    });
  };

  const CANCEL_REASONS = [
    'Too expensive',
    'Missing features I need',
    'Switching to another product',
    'No longer need it / business closing',
    'Too difficult to use',
    'Technical issues or bugs',
    'Other',
  ];

  const handleCancelSubscription = async () => {
    if (!cancelReason) {
      toast.error('Please select a reason for cancelling.');
      return;
    }
    setCancelLoading(true);
    try {
      const response = await api.post('/billing/cancel-subscription', {
        reason: cancelReason,
        comment: cancelComment,
      });
      toast.success(response.data.message || 'Your subscription has been cancelled.');
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelComment('');
      const billingRes = await api.get('/billing/usage');
      setBillingData(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setResumeLoading(true);
    try {
      const response = await api.post('/billing/resume-subscription');
      toast.success(response.data.message || 'Your subscription has been resumed.');
      const billingRes = await api.get('/billing/usage');
      setBillingData(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume subscription');
    } finally {
      setResumeLoading(false);
    }
  };

  const handleModifyExtras = async (resourceType, newQuantity, licenceType = 'full') => {
    if (!billingData) return;
    let currentExtra;
    if (resourceType === 'operator') {
      currentExtra = licenceType === 'timesheet'
        ? (billingData.operators_timesheet_extra || 0)
        : (billingData.operators_extra || 0);
    } else {
      currentExtra = billingData.users_extra || 0;
    }
    if (newQuantity === currentExtra) return;

    // Fetch charge preview from backend
    setCheckoutLoading(true);
    let preview;
    try {
      const res = await api.post('/billing/preview-extras', {
        resource_type: resourceType,
        quantity: newQuantity,
        licence_type: licenceType,
      });
      preview = res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load charge preview');
      setCheckoutLoading(false);
      return;
    }
    setCheckoutLoading(false);

    const isIncrease = preview.direction === 'increase';
    const label = resourceType === 'operator'
      ? (licenceType === 'timesheet' ? 'timesheet licence' : 'mobile licence')
      : 'user';
    const plural = Math.abs(preview.quantity_change) !== 1 ? 's' : '';

    if (isIncrease) {
      const isTrial = preview.is_trial;
      const lines = [
        `Adding ${preview.quantity_change} ${label}${plural} (${preview.current_quantity} → ${preview.new_quantity})`,
      ];
      if (isTrial) {
        lines.push('', preview.is_onboarding
          ? 'No charge — free during on-boarding.'
          : 'No charge — free during your trial period.');
        if (preview.trial_end) {
          lines.push(`Billing starts after your trial ends on ${new Date(preview.trial_end).toLocaleDateString()}.`);
        }
      } else {
        lines.push(
          '',
          `Per-unit price: $${preview.unit_price?.toFixed(2)}/month`,
          `Monthly increase: +$${preview.monthly_increase?.toFixed(2)}/month`,
        );
        if (preview.prorated_charge > 0) {
          lines.push(`Pro-rated charge now: $${preview.prorated_charge?.toFixed(2)} (remainder of current period)`);
        }
        if (preview.next_billing_date) {
          lines.push(`Next billing date: ${new Date(preview.next_billing_date).toLocaleDateString()}`);
        }
      }
      if (preview.cancels_pending_decrease) {
        lines.push('', 'Note: This will cancel your previously scheduled reduction for this resource.');
      }
      setConfirmDialog({
        open: true,
        title: `Add ${preview.quantity_change} ${label}${plural}?`,
        description: lines.join('\n'),
        onConfirm: async () => {
          setConfirmDialog(d => ({ ...d, open: false }));
          await _doModifyExtras(resourceType, newQuantity, licenceType);
        },
      });
    } else {
      // Decrease
      const isTrial = preview.is_trial;
      const lines = [
        `Removing ${Math.abs(preview.quantity_change)} ${label}${plural} (${preview.current_quantity} → ${preview.new_quantity})`,
      ];
      if (isTrial) {
        // Free period: change applies immediately, no billing lines
        if (preview.note) lines.push('', preview.note);
      } else {
        lines.push(
          '',
          `Monthly decrease: -$${preview.monthly_decrease?.toFixed(2)}/month`,
          '',
          'No credit or refund applies.',
        );
        if (preview.effective_date) {
          lines.push(`You keep the current count until ${new Date(preview.effective_date).toLocaleDateString()}.`);
        } else {
          lines.push('The change takes effect at the end of your current billing period.');
        }
      }
      setConfirmDialog({
        open: true,
        title: `Reduce ${label}${plural}?`,
        description: lines.join('\n'),
        onConfirm: async () => {
          setConfirmDialog(d => ({ ...d, open: false }));
          await _doModifyExtras(resourceType, newQuantity, licenceType);
        },
      });
    }
  };

  const _doModifyExtras = async (resourceType, newQuantity, licenceType) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/modify-extras', {
        resource_type: resourceType,
        quantity: newQuantity,
        licence_type: licenceType
      });
      toast.success(response.data.message);
      // Refresh billing data
      const billingRes = await api.get('/billing/usage');
      setBillingData(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify subscription');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Trial / on-boarding tenants change licence counts for free — never via Stripe Checkout.
  const canModifyExtrasInline = () => Boolean(
    billingData && (
      billingData.stripe_customer_id ||
      billingData.trial?.is_trial ||
      ['trialing', 'onboarding'].includes(billingData.subscription_status)
    )
  );

  const handlePurchaseExtra = async (resourceType, quantity) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/checkout/extra-resource', {
        resource_type: resourceType,
        quantity: quantity,
        origin_url: window.location.origin
      });
      if (response.data.inline) {
        // Fast-path: items added inline, no redirect needed
        toast.success(response.data.message || 'Extras updated');
        const billingRes = await api.get('/billing/usage');
        setBillingData(billingRes.data);
        setCheckoutLoading(false);
      } else {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handlePurchaseStorage = async () => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/storage/purchase-addon');
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Storage add-on is not currently available');
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cancellation_reasons: (form.cancellation_reasons || []).map(r => (r || '').trim()).filter(Boolean),
      };
      await api.put(`/tenants/${user.client_id}`, payload);
      // Signal other pages (Schedule, PlanningBoard) to re-fetch tenant settings
      sessionStorage.setItem('tenant_settings_updated', Date.now().toString());
      invalidateTenantSettings();
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDigest = async () => {
    setSavingDigest(true);
    try {
      await api.put(`/tenants/${user.client_id}`, {
        compliance_digest_enabled: digestEnabled,
        compliance_digest_frequency: digestFrequency,
        compliance_digest_recipients: digestRecipients,
      });
      toast.success('Compliance digest settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save digest settings');
    } finally {
      setSavingDigest(false);
    }
  };

  // User Management handlers
  const handleOpenUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        email: userToEdit.email || '',
        full_name: userToEdit.full_name,
        roles: getUserRoles(userToEdit),
        send_invite: false
      });
    } else {
      setEditingUser(null);
      setUserForm({ email: '', full_name: '', roles: ['dispatcher'], send_invite: false });
    }
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setEditingUser(null);
    setUserForm({ email: '', full_name: '', roles: ['dispatcher'], send_invite: false });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormLoading(true);
    try {
      const response = await api.post('/users', {
        email: userForm.email,
        full_name: userForm.full_name,
        roles: userForm.roles,
        send_invite: userForm.send_invite
      });
      toast.success(response.data.message || 'User created successfully');
      handleCloseUserModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleUpdateUser = async (e, confirmCrossType = false, operatorEmailAction = null) => {
    if (e) e.preventDefault();
    if (!editingUser) return;
    setUserFormLoading(true);
    try {
      const payload = {
        email: userForm.email,
        full_name: userForm.full_name,
      };
      // Only send roles when editing someone else (not self)
      if (editingUser.id !== user?.id) {
        payload.roles = userForm.roles;
      }
      if (confirmCrossType) {
        payload.confirm_cross_type = true;
      }
      if (operatorEmailAction) {
        payload.operator_email_action = operatorEmailAction;
      }
      await api.put(`/users/${editingUser.id}`, payload);
      toast.success(
        operatorEmailAction === 'split'
          ? 'User updated — the worker was kept on their existing email as a separate login'
          : 'User updated successfully'
      );
      handleCloseUserModal();
      setCrossTypeDialog({ open: false, match: null });
      setLinkedOperatorDialog({ open: false, worker: null, message: '', confirmCrossType: false });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle cross-type warning (409 with match info)
      if (error.response?.status === 409 && detail?.type === 'cross_type_warning') {
        setCrossTypeDialog({ open: true, match: detail.match, message: detail.message });
      } else if (error.response?.status === 409 && detail?.type === 'linked_operator_email') {
        // Desktop user shares this email with a linked worker — ask how to handle it
        setLinkedOperatorDialog({ open: true, worker: detail.worker, message: detail.message, confirmCrossType });
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to update user');
      }
    } finally {
      setUserFormLoading(false);
    }
  };

  // -- Operator edit from Settings > Users --
  const handleOpenEditOperator = (op) => {
    setEditOperatorForm({ name: op.full_name || op.name || '', email: op.email || '' });
    setEditOperatorModal({ open: true, operator: op });
  };

  const handleUpdateOperator = async (confirmCrossType = false) => {
    const op = editOperatorModal.operator;
    if (!op) return;
    setEditOperatorLoading(true);
    try {
      // Fetch full operator record to send all required fields
      const { data: fullOp } = await api.get(`/operators/${op.operator_id || op.id}`);
      const payload = {
        ...fullOp,
        name: editOperatorForm.name,
        email: editOperatorForm.email,
      };
      if (confirmCrossType) payload.confirm_cross_type = true;
      await api.put(`/operators/${op.operator_id || op.id}`, payload);
      toast.success('Operator updated');
      setEditOperatorModal({ open: false, operator: null });
      setOperatorCrossTypeDialog({ open: false, match: null, message: '' });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.type === 'cross_type_warning') {
        setOperatorCrossTypeDialog({ open: true, match: detail.match, message: detail.message });
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to update operator');
      }
    } finally {
      setEditOperatorLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      toast.success('Password reset successfully');
      setTempPasswordModal({
        open: true,
        password: response.data.temp_password,
        username: response.data.username
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.put(`/users/${userId}/status`, { is_active: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleViewActivity = async (userToView) => {
    setActivityLoading(true);
    setActivityModal({ open: true, user: userToView, logs: [] });
    try {
      const response = await api.get(`/users/${userToView.id}/activity`);
      setActivityModal({ open: true, user: response.data.user, logs: response.data.activity_logs });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const [migrateModal, setMigrateModal] = useState({ open: false, user: null, email: '' });
  const [migrateLoading, setMigrateLoading] = useState(false);

  const handleMigrateUser = async () => {
    if (!migrateModal.user) return;
    setMigrateLoading(true);
    try {
      const res = await api.post(`/users/${migrateModal.user.id}/migrate`, {
        email: migrateModal.email || undefined
      });
      toast.success(res.data.message || 'User migrated successfully');
      setMigrateModal({ open: false, user: null, email: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to migrate user');
    } finally {
      setMigrateLoading(false);
    }
  };

  const [setupEmailModal, setSetupEmailModal] = useState({ open: false, operator: null, email: '' });
  const [setupEmailLoading, setSetupEmailLoading] = useState(false);

  const handleSetupOperatorEmail = async () => {
    if (!setupEmailModal.operator) return;
    setSetupEmailLoading(true);
    try {
      const opId = setupEmailModal.operator.operator_id || setupEmailModal.operator.id;
      const res = await api.post(`/users/operators/${opId}/setup-email-login`, {
        email: setupEmailModal.email
      });
      toast.success(res.data.message || 'Email login configured');
      setSetupEmailModal({ open: false, operator: null, email: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup email login');
    } finally {
      setSetupEmailLoading(false);
    }
  };

  const handleSendInvite = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/send-invite`);
      toast.success(response.data.message || 'Invitation sent');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  // Handle password change
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or WEBP image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/tenants/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(res.data.logo_url);
      toast.success('Logo uploaded');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setLogoUploading(true);
    try {
      await api.delete('/tenants/logo');
      setLogoUrl(null);
      toast.success('Logo removed');
    } catch {
      toast.error('Failed to remove logo');
    } finally {
      setLogoUploading(false);
    }
  };


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/\d/.test(passwordForm.newPassword)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const canAddMoreUsers = () => {
    if (!billingData) return true;
    return (billingData.users_used || 0) < (billingData.users_total_limit || 1);
  };

  const getRoleBadge = (role) => {
    const colors = ROLE_COLORS[role] || ROLE_COLORS.viewer;
    const label = ROLE_LABELS[role] || role;
    const iconMap = {
      admin: Shield,
      dispatcher: UserCog,
      accounts: Calculator,
      compliance_officer: Shield,
      viewer: Eye,
      operator: User,
    };
    const Icon = iconMap[role] || User;
    return (
      <Badge className={`${colors.bg} ${colors.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const getRoleBadges = (userObj) => {
    const roles = getUserRoles(userObj);
    if (roles.length <= 1) return getRoleBadge(roles[0] || 'viewer');
    return (
      <div className="flex flex-wrap gap-1">
        {roles.map(r => (
          <span key={r}>{getRoleBadge(r)}</span>
        ))}
      </div>
    );
  };

  const getStatusBadge = (isActive) => {
    if (isActive === false) {
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          <UserX className="h-3 w-3 mr-1" />
          Inactive
        </Badge>
      );
    }
    return null;
  };

  const formatActivityAction = (action) => {
    const actionMap = {
      'created': { label: 'Account Created', color: 'text-green-600' },
      'updated': { label: 'Details Updated', color: 'text-blue-600' },
      'password_reset': { label: 'Password Reset', color: 'text-orange-600' },
      'activated': { label: 'Activated', color: 'text-green-600' },
      'deactivated': { label: 'Deactivated', color: 'text-red-600' },
      'deleted': { label: 'Deleted', color: 'text-red-600' },
      'login': { label: 'Logged In', color: 'text-gray-600' }
    };
    return actionMap[action] || { label: action, color: 'text-gray-600' };
  };

  const filteredUsers = users.filter(u =>
    !u.is_operator &&
    ((u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const operatorUsers = users.filter(u =>
    u.is_operator &&
    ((u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const legacyUsersCount = users.filter(u => u.needs_migration).length;

  const userSortAccessors = {
    name: (u) => (u.full_name || '').toLowerCase(),
    email: (u) => (u.email || '').toLowerCase(),
    role: (u) => getUserRoles(u).map(r => ROLE_LABELS[r] || r).join(', ').toLowerCase(),
    status: (u) => (u.is_active === false ? 'inactive' : 'active'),
  };
  const { sortedData: sortedFilteredUsers, requestSort: requestUserSort, getSortDirection: getUserSortDirection } = useSortableTable(filteredUsers, userSortAccessors);
  const { sortedData: sortedOperatorUsers, requestSort: requestOperatorUserSort, getSortDirection: getOperatorUserSortDirection } = useSortableTable(operatorUsers, userSortAccessors);

  const handleSavePayCycle = async () => {
    setSavingPayCycle(true);
    try {
      await api.put(`/tenants/${user.client_id}`, {
        pay_cycle_type: form.pay_cycle_type,
        pay_cycle_day: form.pay_cycle_day,
        pay_cycle_start_date: form.pay_cycle_start_date || null,
        payroll_calc_method: form.payroll_calc_method,
        overtime_threshold_hours: form.overtime_threshold_hours,
        overtime_1_5_threshold_hours: form.overtime_1_5_threshold_hours,
      });
      toast.success('Pay cycle settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pay cycle settings');
    } finally {
      setSavingPayCycle(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{isSuspended ? 'Billing' : 'Settings'}</h1>
        <p className="text-muted-foreground">
          {isSuspended ? 'Update your plan to restore full access' : 'Manage your organization settings and users'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          {!isSuspended && (
            <TabsTrigger value="organization" data-testid="tab-organization">
              <Building2 className="h-4 w-4 mr-2" />
              Organization
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="ai" data-testid="tab-ai" className="relative">
              <Brain className="h-4 w-4 mr-2" />
              AI Integration
              {!hasTierFeature('ai_features') && <Lock className="h-3 w-3 ml-1 text-muted-foreground" />}
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="accounting" data-testid="tab-accounting">
              <Calculator className="h-4 w-4 mr-2" />
              Accounting
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="timesheets" data-testid="tab-timesheets">
              <Clock className="h-4 w-4 mr-2" />
              Timesheets
            </TabsTrigger>
          )}
          <TabsTrigger value="billing" data-testid="tab-billing">
            <CreditCard className="h-4 w-4 mr-2" />
            Billing
          </TabsTrigger>
          {!isSuspended && (
            <TabsTrigger value="users" data-testid="tab-users">
              <UsersIcon className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="account" data-testid="tab-account">
              <User className="h-4 w-4 mr-2" />
              My Account
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="support" data-testid="tab-support">
              <HelpCircle className="h-4 w-4 mr-2" />
              Support
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="rules" data-testid="tab-rules">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Rules
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
          )}
          {!isSuspended && (
            <TabsTrigger value="docket-pdf" data-testid="tab-docket-pdf">
              <FileText className="h-4 w-4 mr-2" />
              Dockets
            </TabsTrigger>
          )}
          {!isSuspended && isDemoTenant && (
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <Webhook className="h-4 w-4 mr-2" />
              Integrations
            </TabsTrigger>
          )}
        </TabsList>

        {/* Organization Settings Tab */}
        <TabsContent value="organization" className="mt-6">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              {/* Company Information */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Company Information
                  </CardTitle>
                  <CardDescription>Basic details about your organization</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Client ID</Label>
                      <Input
                        value={form.client_id}
                        disabled
                        className="font-mono bg-muted"
                        data-testid="settings-client-id"
                      />
                      <p className="text-xs text-muted-foreground">
                        Your unique organization identifier (cannot be changed)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Company Name</Label>
                      <Input
                        value={form.company_name}
                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                        placeholder="Your Company Name"
                        required
                        data-testid="settings-company-name"
                      />
                      <p className="text-xs text-muted-foreground">
                        Your operating / trading name, shown across the app
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Legal Entity Name</Label>
                      <Input
                        value={form.legal_entity_name}
                        onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })}
                        placeholder="Registered legal business name"
                        data-testid="settings-legal-entity-name"
                      />
                      <p className="text-xs text-muted-foreground">
                        Your registered legal business name (optional)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">ABN / Business Number</Label>
                      <Input
                        value={form.business_number}
                        onChange={(e) => setForm({ ...form, business_number: e.target.value })}
                        placeholder="e.g. 12 345 678 901"
                        data-testid="settings-business-number"
                      />
                      <p className="text-xs text-muted-foreground">
                        Your ABN or company registration number (optional)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Phone Number</Label>
                      <Input
                        value={form.phone_number}
                        onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                        placeholder="e.g. +61 2 1234 5678"
                        data-testid="settings-phone-number"
                      />
                      <p className="text-xs text-muted-foreground">
                        Company phone number (shown on dockets when enabled)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Business Address</Label>
                      <Textarea
                        value={form.business_address}
                        onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                        placeholder="Street address, City, State, Postcode"
                        rows={2}
                        data-testid="settings-business-address"
                      />
                      <p className="text-xs text-muted-foreground">
                        Company business address (shown on dockets when enabled)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Industry</Label>
                      <Select
                        value={form.industry || 'crane_hire'}
                        onValueChange={(value) => setForm({ ...form, industry: value })}
                      >
                        <SelectTrigger data-testid="settings-industry">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="crane_hire">Crane Hire</SelectItem>
                          <SelectItem value="earthmoving">Earthmoving</SelectItem>
                          <SelectItem value="haulage">Haulage</SelectItem>
                          <SelectItem value="concrete_pumping">Concrete Pumping</SelectItem>
                          <SelectItem value="street_sweeping">Street Sweeping</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Determines available equipment types and features
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Logo */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Company Logo
                  </CardTitle>
                  <CardDescription>Displayed in the sidebar and on generated PDFs (dockets, quotes)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6">
                    {/* Preview */}
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/20 flex-shrink-0" data-testid="logo-preview">
                      {logoUrl ? (
                        <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
                      )}
                    </div>
                    {/* Actions */}
                    <div className="space-y-3 flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={handleLogoUpload}
                        data-testid="logo-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        data-testid="upload-logo-btn"
                      >
                        {logoUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                        {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                      </Button>
                      {logoUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={handleLogoDelete}
                          disabled={logoUploading}
                          data-testid="delete-logo-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Remove
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">PNG, JPG or WEBP. Max 2MB. Recommended: square or landscape.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Location & Timezone */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Location & Time Zone
                  </CardTitle>
                  <CardDescription>Set your country and timezone for accurate date, time, and address display</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Country</Label>
                      <Select
                        value={form.country}
                        onValueChange={(value) => setForm({ ...form, country: value })}
                      >
                        <SelectTrigger data-testid="settings-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Affects address autocomplete, state/province lists, and postal code format
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Time Zone</Label>
                      <Select
                        value={form.timezone}
                        onValueChange={(value) => setForm({ ...form, timezone: value })}
                      >
                        <SelectTrigger data-testid="settings-timezone">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((group) => (
                            <div key={group.group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                                {group.group}
                              </div>
                              {group.zones.map((zone) => (
                                <SelectItem key={zone.value} value={zone.value}>
                                  {zone.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Current time: {(() => {
                          try {
                            // Strip any invisible/non-ASCII garbage from the stored value
                            const tz = (form.timezone || '').replace(/[^\x20-\x7E\/\_]/g, '').trim();
                            if (!tz) return '—';
                            const now = new Date();
                            const opts = { timeZone: tz, hour: '2-digit', minute: '2-digit' };
                            if (form.time_format === '12h') {
                              return now.toLocaleTimeString('en-US', { ...opts, hour12: true });
                            }
                            return now.toLocaleTimeString('en-AU', { ...opts, hour12: false });
                          } catch {
                            return '(invalid timezone)';
                          }
                        })()}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Date Format</Label>
                      <Select
                        value={form.date_format}
                        onValueChange={(value) => setForm({ ...form, date_format: value })}
                      >
                        <SelectTrigger data-testid="settings-date-format">
                          <SelectValue placeholder="Select date format" />
                        </SelectTrigger>
                        <SelectContent>
                          {DATE_FORMATS.map((format) => (
                            <SelectItem key={format.value} value={format.value}>
                              {format.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Locale / Region</Label>
                      <Select
                        value={form.locale}
                        onValueChange={(value) => setForm({ ...form, locale: value })}
                      >
                        <SelectTrigger data-testid="settings-locale">
                          <SelectValue placeholder="Select locale" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORTED_LOCALES.map((loc) => (
                            <SelectItem key={loc.value} value={loc.value}>
                              {loc.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Controls labels like Post Code vs Zip Code, Suburb vs City, GST vs Sales Tax
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Working Hours */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Default Working Hours
                  </CardTitle>
                  <CardDescription>Standard operating hours for your business</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 max-w-2xl grid-cols-2">
                    <div className="space-y-2">
                      <Label className="label-overline">Start Time</Label>
                      <TimeField
                        value={form.default_working_hours.start}
                        onChange={(v) => setForm({
                          ...form,
                          default_working_hours: { ...form.default_working_hours, start: v }
                        })}
                        data-testid="settings-start-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">End Time</Label>
                      <TimeField
                        value={form.default_working_hours.end}
                        onChange={(v) => setForm({
                          ...form,
                          default_working_hours: { ...form.default_working_hours, end: v }
                        })}
                        data-testid="settings-end-time"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Currency & Tax */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Currency & Tax
                  </CardTitle>
                  <CardDescription>Configure currency and tax settings for invoicing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Currency</Label>
                      <Select
                        value={form.currency}
                        onValueChange={(value) => {
                          const currency = CURRENCIES.find(c => c.code === value);
                          setForm({ 
                            ...form, 
                            currency: value,
                            currency_symbol: currency?.symbol || '$'
                          });
                        }}
                      >
                        <SelectTrigger data-testid="settings-currency">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code} - {currency.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Currency Symbol</Label>
                      <Input
                        value={form.currency_symbol}
                        onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                        placeholder="$"
                        className="font-mono w-20"
                        maxLength={3}
                        data-testid="settings-currency-symbol"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Tax Type</Label>
                      <Select
                        value={form.tax_name}
                        onValueChange={(value) => setForm({ ...form, tax_name: value })}
                      >
                        <SelectTrigger data-testid="settings-tax-name">
                          <SelectValue placeholder="Select tax type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TAX_TYPES.map((tax) => (
                            <SelectItem key={tax.name} value={tax.name}>
                              {tax.name} - {tax.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Tax Rate (%)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={form.tax_rate}
                          onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                          placeholder="10"
                          className="font-mono pr-8"
                          data-testid="settings-tax-rate"
                        />
                        <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      </div>
                      {form.tax_name !== 'None' && form.tax_rate > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {form.tax_name} at {form.tax_rate}% will be added to docket totals
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Info */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5" />
                    System Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="label-overline">Current User</p>
                      <p className="font-medium">{user?.full_name}</p>
                    </div>
                    <div>
                      <p className="label-overline">Role</p>
                      <p className="font-medium capitalize">{getUserRoles(user).map(r => ROLE_LABELS[r] || r).join(', ')}</p>
                    </div>
                    <div>
                      <p className="label-overline">Username</p>
                      <p className="font-mono">{user?.username}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button type="submit" className="btn-volt" disabled={saving} data-testid="save-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </form>
        </TabsContent>

        {/* AI Integration Tab */}
        <TabsContent value="ai" className="mt-6">
          {!hasTierFeature('ai_features') ? (
            <UpgradePrompt 
              feature="ai_features"
              title="AI Integration"
              description="Unlock powerful AI features including Docket OCR scanning, Smart Operator Suggestions, and intelligent Compliance Alerts. Available exclusively on the Professional plan."
            />
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    AI Features
                  </CardTitle>
                  <CardDescription>
                    Enable AI-powered features like Docket OCR, Smart Scheduling, and Compliance Alerts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Master AI Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-[#ffd108]/5 to-transparent">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${form.ai_enabled ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <Power className={`h-5 w-5 ${form.ai_enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <Label className="text-base font-semibold">AI Features</Label>
                        <p className="text-sm text-muted-foreground">
                          {form.ai_enabled ? 'AI features are enabled for your organization' : 'Enable to use AI-powered features'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={form.ai_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, ai_enabled: checked })}
                      data-testid="ai-enabled-toggle"
                    />
                  </div>

                  <div className={`p-4 bg-muted/50 rounded-lg transition-opacity ${!form.ai_enabled ? 'opacity-50' : ''}`}>
                    <h4 className="font-semibold mb-2">Available AI Features:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Docket OCR</strong> - Scan paper dockets and auto-extract data
                      </li>
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Compliance Alerts</strong> - AI-powered expiry warnings
                      </li>
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Smart Suggestions</strong> - Intelligent operator/equipment recommendations
                      </li>
                    </ul>
                  </div>

                  {form.ai_enabled && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Key className="h-4 w-4" />
                          OpenAI API Key
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              type={showApiKey ? 'text' : 'password'}
                              value={form.openai_api_key}
                              onChange={(e) => {
                                setForm({ ...form, openai_api_key: e.target.value });
                                setApiKeyValid(null);
                              }}
                              placeholder="sk-..."
                              className="font-mono pr-10"
                              data-testid="settings-openai-key"
                            />
                            <button
                              type="button"
                              onClick={() => setShowApiKey(!showApiKey)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {apiKeyValid === true && (
                            <div className="flex items-center text-green-600">
                              <CheckCircle2 className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Your API key is stored securely and only used for AI features within your organization.
                        </p>
                      </div>

                      <div className="flex items-center gap-4 pt-2">
                        <a
                          href="https://platform.openai.com/api-keys"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          Get an API key from OpenAI
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>

                      {!form.openai_api_key && (
                        <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                          <p className="text-sm text-amber-800">
                            <strong>Note:</strong> Some AI features (like Docket OCR) require an OpenAI API key. Smart Suggestions work without a key.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {!form.ai_enabled && (
                    <div className="p-4 border border-muted bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        AI features are currently disabled. Enable the toggle above to access Smart Suggestions, Docket OCR, and Compliance Alerts.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button type="submit" className="btn-volt" disabled={saving} data-testid="save-ai-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>

              {/* Compliance Email Digest (Professional) */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Compliance Email Digest
                  </CardTitle>
                  <CardDescription>
                    Receive scheduled email summaries of expiring compliance items
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-[#ffd108]/5 to-transparent">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${digestEnabled ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <Bell className={`h-5 w-5 ${digestEnabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <Label className="text-base font-semibold">Email Digest</Label>
                        <p className="text-sm text-muted-foreground">
                          {digestEnabled ? 'Compliance digest emails are active' : 'Enable to receive compliance summary emails'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={digestEnabled}
                      onCheckedChange={setDigestEnabled}
                      data-testid="compliance-digest-toggle"
                    />
                  </div>

                  {digestEnabled && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="label-overline">Frequency</Label>
                        <Select value={digestFrequency} onValueChange={setDigestFrequency}>
                          <SelectTrigger data-testid="compliance-digest-frequency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily (every morning)</SelectItem>
                            <SelectItem value="weekly">Weekly (every Monday)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="label-overline">Recipients</Label>
                        <p className="text-xs text-muted-foreground mb-2">Select which users should receive the compliance digest email</p>
                        <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2">
                          {users.filter(u => u.email).map(u => (
                            <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer">
                              <Checkbox
                                checked={digestRecipients.includes(u.email)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setDigestRecipients(prev => [...prev, u.email]);
                                  } else {
                                    setDigestRecipients(prev => prev.filter(e => e !== u.email));
                                  }
                                }}
                                data-testid={`digest-recipient-${u.id}`}
                              />
                              <span className="text-sm">{u.full_name || u.username}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{u.email}</span>
                            </label>
                          ))}
                          {users.filter(u => u.email).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-2">No users with email addresses found</p>
                          )}
                        </div>
                      </div>

                      <div className="p-3 bg-muted/30 rounded-lg text-sm text-muted-foreground">
                        <p>The digest includes overdue items, items expiring within 7, 14, and 30 days, grouped by operator, equipment, and supplier.</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSaveDigest} className="btn-volt" disabled={savingDigest} data-testid="save-digest-settings-btn">
                      <Save className="h-4 w-4 mr-2" />
                      {savingDigest ? 'Saving...' : 'Save Digest Settings'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </form>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          {!isAdminUser(user) ? (
            <Card className="card-industrial">
              <CardContent className="py-12 text-center">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">User management is only available to administrators.</p>
              </CardContent>
            </Card>
          ) : (
          <div className="space-y-6">
            {/* User Limits Banner */}
            {billingData && (
              <Card className={`card-industrial ${(billingData.users_used || 0) >= (billingData.users_total_limit || 1) ? 'border-destructive' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UsersIcon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">
                          User Seats: {filteredUsers.filter(u => u.is_active !== false).length} / {billingData.users_total_limit || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {billingData.tier_name || 'Basic'} Plan ({billingData.users_limit || 0} included
                          {(billingData.users_extra || 0) > 0 && ` + ${billingData.users_extra} extra`})
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                        <Badge variant="destructive" className="mr-2">Limit Reached</Badge>
                      )}
                      <Button 
                        onClick={() => handleOpenUserModal()}
                        disabled={!canAddMoreUsers()}
                        className="btn-volt"
                        data-testid="add-user-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add User
                      </Button>
                    </div>
                  </div>
                  {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                    <p className="text-xs text-destructive mt-2">
                      You have reached your user limit. Purchase additional seats or upgrade your plan in the Billing tab.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Legacy Migration Notice */}
            {legacyUsersCount > 0 && (
              <Card className="card-industrial border-yellow-500/30">
                <CardContent className="py-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-500">{legacyUsersCount} Legacy User{legacyUsersCount > 1 ? 's' : ''} Detected</p>
                      <p className="text-xs text-muted-foreground">
                        These users authenticate via Client ID login and haven't been migrated to email-based auth. 
                        Use the Auth Migration tool in Data Management to upgrade them.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Users List */}
            <Card className="card-industrial">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg">
                    {filteredUsers.length} User{filteredUsers.length !== 1 ? 's' : ''}
                  </CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="user-search"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="animate-pulse h-48 bg-muted rounded" />
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No users found</p>
                    <Button onClick={() => handleOpenUserModal()} className="mt-4" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First User
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead sortKey="name" requestSort={requestUserSort} getSortDirection={getUserSortDirection} className="label-overline w-[15%]">Name</SortableTableHead>
                          <SortableTableHead sortKey="email" requestSort={requestUserSort} getSortDirection={getUserSortDirection} className="label-overline w-[30%]">Email</SortableTableHead>
                          <SortableTableHead sortKey="role" requestSort={requestUserSort} getSortDirection={getUserSortDirection} className="label-overline w-[12%]">Role</SortableTableHead>
                          <SortableTableHead sortKey="status" requestSort={requestUserSort} getSortDirection={getUserSortDirection} className="label-overline w-[12%]">Status</SortableTableHead>
                          <TableHead className="label-overline w-[16%]">Invite</TableHead>
                          <TableHead className="label-overline w-[15%] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedFilteredUsers.map((u) => (
                          <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={u.is_active === false ? 'opacity-60' : ''}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {u.full_name}
                                {u.id === user?.id && (
                                  <Badge variant="outline" className="text-xs">You</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{u.email || '-'}</TableCell>
                            <TableCell>{getRoleBadges(u)}</TableCell>
                            <TableCell>
                              {u.is_active === false ? (
                                <Badge variant="secondary" className="bg-red-100 text-red-800">
                                  <UserX className="h-3 w-3 mr-1" />
                                  Inactive
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.auth_type === 'v2' ? (
                                u.invite_accepted ? (
                                  <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">Accepted</Badge>
                                ) : u.invite_sent ? (
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">Sent</Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-xs">Not Sent</Badge>
                                )
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">Legacy</Badge>
                                  {u.needs_migration && (
                                    <Badge variant="secondary" className="bg-yellow-50 text-yellow-600 text-xs border border-yellow-300">
                                      Needs Migration
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {u.id !== user?.id ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      data-testid={`user-actions-${u.id}`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleOpenUserModal(u)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit User
                                    </DropdownMenuItem>
                                    {(!u.invite_sent || u.auth_type === 'v2') && !u.invite_accepted && u.email && (
                                      <DropdownMenuItem onClick={() => handleSendInvite(u.id)}>
                                        <Mail className="h-4 w-4 mr-2" />
                                        {u.invite_sent ? 'Resend Invite' : 'Send Invite'}
                                      </DropdownMenuItem>
                                    )}
                                    {u.auth_type === 'legacy' && (
                                      <DropdownMenuItem onClick={() => handleResetPassword(u.id)}>
                                        <KeyRound className="h-4 w-4 mr-2" />
                                        Reset Password
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => handleViewActivity(u)}>
                                      <History className="h-4 w-4 mr-2" />
                                      View Activity
                                    </DropdownMenuItem>
                                    {u.needs_migration && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setMigrateModal({ open: true, user: u, email: u.email || '' })}>
                                          <ArrowUpCircle className="h-4 w-4 mr-2" />
                                          Migrate to Email Auth
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleUserStatus(u.id, u.is_active !== false)}>
                                      {u.is_active === false ? (
                                        <>
                                          <UserCheck className="h-4 w-4 mr-2" />
                                          Activate User
                                        </>
                                      ) : (
                                        <>
                                          <UserX className="h-4 w-4 mr-2" />
                                          Deactivate User
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setDeleteConfirm({ open: true, user: u })}
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`delete-user-${u.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete User
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenUserModal(u)}
                                  data-testid="edit-self-btn"
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Operator Users Section */}
            {operatorUsers.length > 0 && (
              <Card className="card-industrial">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg">
                      {operatorUsers.length} Operator{operatorUsers.length !== 1 ? 's' : ''}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">Managed via Operators page</p>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead sortKey="name" requestSort={requestOperatorUserSort} getSortDirection={getOperatorUserSortDirection} className="label-overline w-[15%]">Name</SortableTableHead>
                          <SortableTableHead sortKey="email" requestSort={requestOperatorUserSort} getSortDirection={getOperatorUserSortDirection} className="label-overline w-[30%]">Email</SortableTableHead>
                          <SortableTableHead sortKey="role" requestSort={requestOperatorUserSort} getSortDirection={getOperatorUserSortDirection} className="label-overline w-[12%]">Role</SortableTableHead>
                          <SortableTableHead sortKey="status" requestSort={requestOperatorUserSort} getSortDirection={getOperatorUserSortDirection} className="label-overline w-[12%]">Status</SortableTableHead>
                          <TableHead className="label-overline w-[16%]">Invite</TableHead>
                          <TableHead className="label-overline w-[15%] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedOperatorUsers.map((u) => (
                          <TableRow key={u.id} className={u.is_active === false ? 'opacity-60' : ''}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {u.full_name || '-'}
                                {u.access_code && (
                                  <Badge variant="outline" className="text-xs font-mono">{u.access_code}</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{u.email || <span className="italic opacity-50">No email</span>}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="bg-purple-100 text-purple-800">Operator</Badge>
                            </TableCell>
                            <TableCell>
                              {u.is_active === false ? (
                                <Badge variant="secondary" className="bg-red-100 text-red-800">
                                  <UserX className="h-3 w-3 mr-1" />
                                  Inactive
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.has_email_login ? (
                                u.invite_accepted ? (
                                  <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">Accepted</Badge>
                                ) : u.invite_sent ? (
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">Invite Sent</Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-xs">Not Sent</Badge>
                                )
                              ) : u.auth_type === 'pin' ? (
                                <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-xs">PIN Only</Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">No Login</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`operator-actions-${u.id}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenEditOperator(u)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Operator
                                  </DropdownMenuItem>
                                  {u.has_email_login ? (
                                    <>
                                      {!u.invite_accepted && (
                                        <DropdownMenuItem onClick={() => handleSendInvite(u.id)}>
                                          <Mail className="h-4 w-4 mr-2" />
                                          {u.invite_sent ? 'Resend Invite' : 'Send Invite'}
                                        </DropdownMenuItem>
                                      )}
                                      {u.invite_accepted && (
                                        <DropdownMenuItem disabled className="opacity-50">
                                          <CheckCircle2 className="h-4 w-4 mr-2" />
                                          Invite Accepted
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  ) : (
                                    <DropdownMenuItem onClick={() => setSetupEmailModal({ open: true, operator: u, email: u.email || '' })}>
                                      <Mail className="h-4 w-4 mr-2" />
                                      Setup Email Login
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Delete User Confirmation Dialog */}
          <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, user: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete User</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>{deleteConfirm.user?.full_name}</strong>?
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    handleDeleteUser(deleteConfirm.user?.id);
                    setDeleteConfirm({ open: false, user: null });
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Migrate User Dialog */}
          <Dialog open={migrateModal.open} onOpenChange={(open) => !open && setMigrateModal({ open: false, user: null, email: '' })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Migrate to Email Auth</DialogTitle>
                <DialogDescription>
                  Migrate <strong>{migrateModal.user?.full_name || migrateModal.user?.username}</strong> from legacy 
                  Client ID login to email-based authentication. They will be able to log in using their email and password.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={migrateModal.email}
                    onChange={(e) => setMigrateModal(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="user@company.com"
                    data-testid="migrate-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    A v2 account will be created with this email. You can then send them a login invite.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMigrateModal({ open: false, user: null, email: '' })}>
                  Cancel
                </Button>
                <Button
                  onClick={handleMigrateUser}
                  disabled={migrateLoading || !migrateModal.email}
                  data-testid="migrate-confirm-btn"
                >
                  {migrateLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowUpCircle className="h-4 w-4 mr-2" />}
                  Migrate User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Setup Operator Email Login Dialog */}
          <Dialog open={setupEmailModal.open} onOpenChange={(open) => !open && setSetupEmailModal({ open: false, operator: null, email: '' })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Setup Email Login</DialogTitle>
                <DialogDescription>
                  Configure email-based login for <strong>{setupEmailModal.operator?.full_name}</strong>. 
                  After confirming the email, you can send them an invite with a link to set their password.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Confirm Email Address</Label>
                  <Input
                    type="email"
                    value={setupEmailModal.email}
                    onChange={(e) => setSetupEmailModal(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="operator@company.com"
                    data-testid="setup-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This email will be used for their login. An invite will need to be sent separately after setup.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupEmailModal({ open: false, operator: null, email: '' })}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSetupOperatorEmail}
                  disabled={setupEmailLoading || !setupEmailModal.email}
                  data-testid="setup-email-confirm-btn"
                >
                  {setupEmailLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Confirm & Setup
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          {/* Add/Edit User Modal */}
          <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? 'Update user details below.' 
                    : 'Create a new user. They will log in using their email and password.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="full_name"
                      value={userForm.full_name}
                      onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      data-testid="user-email-input"
                    />
                    {editingUser && userForm.email !== (editingUser.email || '') && (
                      <p className="text-xs text-amber-600">Changing the email will update their login credentials.</p>
                    )}
                  </div>
                  {/* Hide role selection when editing yourself */}
                  {!(editingUser && editingUser.id === user?.id) && (
                  <div className="space-y-2">
                    <Label>Roles <span className="text-red-500">*</span></Label>
                    <div className="space-y-2 border rounded-lg p-3">
                      {ASSIGNABLE_ROLES.map((roleId) => {
                        const colors = ROLE_COLORS[roleId] || {};
                        const isChecked = (userForm.roles || []).includes(roleId);
                        return (
                          <label
                            key={roleId}
                            className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                              isChecked ? `${colors.bg} ${colors.border} border` : 'hover:bg-muted border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const current = userForm.roles || [];
                                const updated = e.target.checked
                                  ? [...current, roleId]
                                  : current.filter(r => r !== roleId);
                                if (updated.length === 0) return;
                                setUserForm({ ...userForm, roles: updated });
                              }}
                              className="h-4 w-4 rounded border-gray-300"
                              data-testid={`role-checkbox-${roleId}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${colors.text || ''}`}>
                                {ROLE_LABELS[roleId]}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {ROLE_DESCRIPTIONS[roleId]}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {userForm.roles?.length > 1 && (
                      <p className="text-xs text-muted-foreground">
                        User will have combined permissions from all selected roles.
                      </p>
                    )}
                  </div>
                  )}
                  {!editingUser && (
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                      <div className="space-y-0.5">
                        <Label htmlFor="send_invite" className="text-sm font-medium">Send Invitation Email</Label>
                        <p className="text-xs text-muted-foreground">
                          User cannot log in until invited
                        </p>
                      </div>
                      <Switch
                        id="send_invite"
                        checked={userForm.send_invite}
                        onCheckedChange={(checked) => setUserForm({ ...userForm, send_invite: checked })}
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseUserModal}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-volt" disabled={userFormLoading}>
                    {userFormLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingUser ? 'Saving...' : 'Creating...'}
                      </>
                    ) : (
                      editingUser ? 'Save Changes' : 'Create User'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Cross-Type Email Confirmation Dialog */}
          <AlertDialog open={crossTypeDialog.open} onOpenChange={(open) => setCrossTypeDialog(prev => ({ ...prev, open }))}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Email Already In Use</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>{crossTypeDialog.message}</p>
                    <p>Are you sure these are the <strong>same person</strong>? If confirmed, the email will be updated.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setCrossTypeDialog({ open: false, match: null })}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleUpdateUser(null, true)}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="confirm-cross-type-btn"
                >
                  Yes, Same Person
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Linked-worker email split/cascade confirmation */}
          <AlertDialog open={linkedOperatorDialog.open} onOpenChange={(open) => setLinkedOperatorDialog(prev => ({ ...prev, open }))}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>This email is shared with a worker</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3">
                    <p>{linkedOperatorDialog.message}</p>
                    {linkedOperatorDialog.worker && (
                      <div className="rounded-md border border-border p-3 text-sm bg-muted/40" data-testid="linked-worker-info">
                        <div className="font-medium text-foreground">{linkedOperatorDialog.worker.name}</div>
                        <div className="text-muted-foreground">Operator code: {linkedOperatorDialog.worker.operator_code || '—'}</div>
                        <div className="text-muted-foreground">Email: {linkedOperatorDialog.worker.email || '—'}</div>
                      </div>
                    )}
                    <p>How should this worker be handled?</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li><strong className="text-foreground">Split</strong> — the worker keeps their current email and becomes a separate login. The desktop user moves to the new email. Recommended if these are different people.</li>
                      <li><strong className="text-foreground">Update worker too</strong> — change the worker's email to the new one as well (keeps them linked as the same person).</li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel onClick={() => setLinkedOperatorDialog({ open: false, worker: null, message: '', confirmCrossType: false })}>
                  Cancel
                </AlertDialogCancel>
                <Button
                  variant="outline"
                  disabled={userFormLoading}
                  onClick={() => handleUpdateUser(null, linkedOperatorDialog.confirmCrossType, 'cascade')}
                  data-testid="linked-operator-cascade-btn"
                >
                  Update worker too
                </Button>
                <Button
                  className="btn-volt"
                  disabled={userFormLoading}
                  onClick={() => handleUpdateUser(null, linkedOperatorDialog.confirmCrossType, 'split')}
                  data-testid="linked-operator-split-btn"
                >
                  Split them
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Edit Operator Dialog */}
          <Dialog open={editOperatorModal.open} onOpenChange={(open) => !open && setEditOperatorModal({ open: false, operator: null })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Operator</DialogTitle>
                <DialogDescription>Update operator name and email.</DialogDescription>
              </DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateOperator(); }}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="op_name">Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="op_name"
                      value={editOperatorForm.name}
                      onChange={(e) => setEditOperatorForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="edit-operator-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="op_email">Email</Label>
                    <Input
                      id="op_email"
                      type="email"
                      value={editOperatorForm.email}
                      onChange={(e) => setEditOperatorForm(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="edit-operator-email-input"
                    />
                    {editOperatorModal.operator && editOperatorForm.email !== (editOperatorModal.operator.email || '') && editOperatorForm.email && (
                      <p className="text-xs text-amber-600">Changing the email will update their login credentials.</p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditOperatorModal({ open: false, operator: null })}>Cancel</Button>
                  <Button type="submit" className="btn-volt" disabled={editOperatorLoading} data-testid="save-operator-edit-btn">
                    {editOperatorLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Operator Cross-Type Confirmation */}
          <AlertDialog open={operatorCrossTypeDialog.open} onOpenChange={(open) => !open && setOperatorCrossTypeDialog(prev => ({ ...prev, open: false }))}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Email Already In Use</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>{operatorCrossTypeDialog.message}</p>
                    <p>Are you sure these are the <strong>same person</strong>?</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleUpdateOperator(true)}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  Yes, Same Person
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Temporary Password Modal */}
          <Dialog open={tempPasswordModal.open} onOpenChange={(open) => setTempPasswordModal({ ...tempPasswordModal, open })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Temporary Password Generated
                </DialogTitle>
                <DialogDescription>
                  Share this temporary password with the user. They will be required to change it on first login.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-muted p-4 rounded-lg space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Username</Label>
                    <p className="font-mono font-medium">{tempPasswordModal.username}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Temporary Password</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background px-3 py-2 rounded font-mono text-lg">
                        {tempPasswordModal.password}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(tempPasswordModal.password)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 mt-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>Make sure to copy this password now. It will not be shown again.</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setTempPasswordModal({ open: false, password: '', username: '' })} className="btn-volt">
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Activity Log Modal */}
          <Dialog open={activityModal.open} onOpenChange={(open) => setActivityModal({ ...activityModal, open })}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Activity Log - {activityModal.user?.full_name}
                </DialogTitle>
                <DialogDescription>
                  Recent activity for this user account
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 max-h-96 overflow-y-auto">
                {activityLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : activityModal.logs.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No activity logs found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activityModal.logs.map((log) => {
                      const actionInfo = formatActivityAction(log.action);
                      return (
                        <div key={log.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className={`p-1.5 rounded-full bg-background ${actionInfo.color}`}>
                            <History className="h-3 w-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${actionInfo.color}`}>
                              {actionInfo.label}
                            </p>
                            {log.details && (
                              <p className="text-xs text-muted-foreground mt-0.5">{log.details}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateTime(log.created_at, form.date_format, form.time_format)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActivityModal({ open: false, user: null, logs: [] })}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          )}
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-6">
          <div className="grid gap-6">
            {billingLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : billingData ? (
              <>
                {/* Subscription Cancelling Banner */}
                {billingData.subscription_cancelling && (
                  <Card className="card-industrial border-2 border-red-500 bg-red-500/10 mb-4" data-testid="subscription-cancelling-banner">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                          <div>
                            <p className="font-medium text-red-600">Subscription cancelled</p>
                            <p className="text-sm text-muted-foreground">
                              Your subscription will not renew. You'll keep full access until{' '}
                              {billingData.subscription_cancel_effective_at
                                ? formatDate(billingData.subscription_cancel_effective_at, form.date_format)
                                : 'the end of your billing period'}
                              , after which your account will be suspended.
                            </p>
                          </div>
                        </div>
                        {isAdminUser(user) && (
                          <Button
                            variant="outline"
                            onClick={handleResumeSubscription}
                            disabled={resumeLoading}
                            data-testid="resume-subscription-button"
                          >
                            {resumeLoading ? 'Resuming...' : 'Resume subscription'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pending Downgrade Banner */}
                {billingData.pending_tier_change && (
                  <Card className="card-industrial border-2 border-amber-500 bg-amber-500/10 mb-4">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-500" />
                        <div>
                          <p className="font-medium">
                            Downgrade to {billingData.pending_tier_change.new_tier_name} scheduled
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Your current {billingData.tier_name} features will remain active until{' '}
                            {billingData.pending_tier_change.effective_date 
                              ? formatDate(billingData.pending_tier_change.effective_date, form.date_format)
                              : 'your next billing cycle'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Trial Period Banner */}
                {billingData.trial && billingData.trial.is_trial && (
                  <Card className="card-industrial border-2 border-green-500 bg-green-500/10 mb-4" data-testid="billing-trial-cta-card">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Gift className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="font-medium text-green-600">
                              Free Trial Active
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Your trial ends on {billingData.trial.trial_end_formatted}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-green-600">
                            {billingData.trial.days_remaining}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            days remaining
                          </div>
                        </div>
                      </div>

                      {/* Add Payment CTA — keep the account active after the trial */}
                      <div className="mt-4 pt-4 border-t border-green-500/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-sm text-muted-foreground">
                          Add a payment method to keep your account active when your trial ends.
                        </p>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <Button
                            onClick={() => handleSubscriptionUpgrade(billingData.tier)}
                            disabled={checkoutLoading}
                            data-testid="billing-add-payment-btn"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                            Add Payment — Subscribe to {billingData.tier_name} ($
                            {(
                              (billingData.base_price || 0) +
                              (billingData.operators_extra || 0) * (billingData.extra_operator_price || 0) +
                              (billingData.users_extra || 0) * (billingData.extra_user_price || 0)
                            ).toFixed(0)}/mo)
                          </Button>
                          <button
                            type="button"
                            onClick={() => document.getElementById('change-plan-section')?.scrollIntoView({ behavior: 'smooth' })}
                            className="text-sm text-primary hover:underline whitespace-nowrap"
                            data-testid="billing-compare-plans-link"
                          >
                            Compare plans
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* No active paid subscription (self-service trial / cancelled / past due) — surface the Add Payment CTA */}
                {!(billingData.trial && billingData.trial.is_trial) && billingData.subscription_status !== 'active' && (() => {
                  const onTrial = billingData.subscription_status === 'trialing' || subscriptionStatus === 'trialing' || (trialEndsAt && new Date(trialEndsAt) > new Date());
                  const trialDaysLeft = trialEndsAt
                    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
                    : null;
                  const accent = onTrial ? 'green' : 'amber';
                  return (
                    <Card className={`card-industrial border-2 mb-4 ${onTrial ? 'border-green-500 bg-green-500/10' : 'border-amber-500 bg-amber-500/10'}`} data-testid="billing-nopayment-cta-card">
                      <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {onTrial
                            ? <Gift className="h-5 w-5 text-green-500 flex-shrink-0" />
                            : <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />}
                          <div>
                            <p className={`font-medium ${onTrial ? 'text-green-600' : 'text-amber-600'}`}>
                              {onTrial
                                ? (trialDaysLeft !== null
                                    ? `Free Trial — ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left`
                                    : 'Free Trial Active')
                                : 'No payment method on file'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Add a payment method to keep your account active{onTrial ? ' after your trial ends' : ''}.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <Button
                            onClick={() => handleSubscriptionUpgrade(billingData.tier)}
                            disabled={checkoutLoading}
                            data-testid="billing-add-payment-btn"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                            Add Payment — Subscribe to {billingData.tier_name} ($
                            {(
                              (billingData.base_price || 0) +
                              (billingData.operators_extra || 0) * (billingData.extra_operator_price || 0) +
                              (billingData.users_extra || 0) * (billingData.extra_user_price || 0)
                            ).toFixed(0)}/mo)
                          </Button>
                          <button
                            type="button"
                            onClick={() => document.getElementById('change-plan-section')?.scrollIntoView({ behavior: 'smooth' })}
                            className="text-sm text-primary hover:underline whitespace-nowrap"
                            data-testid="billing-compare-plans-link-2"
                          >
                            Compare plans
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Current Plan */}
                <Card className="card-industrial border-2 border-primary">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <Crown className="h-5 w-5 text-[#ffd108]" />
                          Current Plan: {billingData.tier_name}
                        </CardTitle>
                        <CardDescription>{billingData.tier_description}</CardDescription>
                      </div>
                      <div className="text-right">
                        <Badge variant="default" className="text-lg px-4 py-1">
                          ${(
                            (billingData.base_price || 0) + 
                            (billingData.operators_extra || 0) * (billingData.extra_operator_price || 0) + 
                            (billingData.users_extra || 0) * (billingData.extra_user_price || 0)
                          ).toFixed(0)}/month
                        </Badge>
                        {(billingData.operators_extra > 0 || billingData.users_extra > 0) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Base ${billingData.base_price}
                            {billingData.operators_extra > 0 && ` + ${billingData.operators_extra}×$${billingData.extra_operator_price}`}
                            {billingData.users_extra > 0 && ` + ${billingData.users_extra}×$${billingData.extra_user_price}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Mobile Operators Usage */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Mobile Operators</span>
                          <span className="text-sm text-muted-foreground">
                            {billingData.operators_used || 0} / {billingData.operators_total_limit || 0}
                            {(billingData.operators_extra || 0) > 0 && (
                              <span className="text-xs text-primary ml-1">(+{billingData.operators_extra} extra)</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              ((billingData.operators_used || 0) / (billingData.operators_total_limit || 1)) > 0.9 
                                ? 'bg-destructive' 
                                : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(((billingData.operators_used || 0) / (billingData.operators_total_limit || 1)) * 100, 100)}%` }}
                          />
                        </div>
                        {(billingData.operators_used || 0) >= (billingData.operators_total_limit || 1) && (
                          <p className="text-xs text-destructive">Mobile operator limit reached! Purchase more or upgrade your plan.</p>
                        )}
                      </div>

                      {/* Users Usage */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Admin/Dispatcher Users</span>
                          <span className="text-sm text-muted-foreground">
                            {billingData.users_used || 0} / {billingData.users_total_limit || 0}
                            {(billingData.users_extra || 0) > 0 && (
                              <span className="text-xs text-primary ml-1">(+{billingData.users_extra} extra)</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              ((billingData.users_used || 0) / (billingData.users_total_limit || 1)) > 0.9 
                                ? 'bg-destructive' 
                                : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(((billingData.users_used || 0) / (billingData.users_total_limit || 1)) * 100, 100)}%` }}
                          />
                        </div>
                        {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                          <p className="text-xs text-destructive">Limit reached! Purchase more or upgrade your plan.</p>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3">Included Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {(billingData.features || []).map((feature) => (
                          feature && (
                          <Badge key={feature} variant="secondary" className="capitalize">
                            <Check className="h-3 w-3 mr-1" />
                            {feature.replace(/_/g, ' ')}{feature === 'api_access' ? ' (Coming Soon)' : ''}
                          </Badge>
                          )
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Billing Summary — Payment Method, Upcoming Invoice, Active Discount */}
                {billingData.stripe_customer_id && (
                  <Card className="card-industrial" data-testid="billing-summary-card">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Receipt className="h-5 w-5" />
                        Billing Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Payment Method */}
                        <div className="space-y-2" data-testid="billing-payment-method">
                          <h4 className="text-sm font-medium flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            Payment Method
                          </h4>
                          {billingData.payment_method ? (
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="font-medium capitalize">
                                {billingData.payment_method.brand || 'Card'} ending in {billingData.payment_method.last4}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Expires {String(billingData.payment_method.exp_month).padStart(2, '0')}/{billingData.payment_method.exp_year}
                              </p>
                            </div>
                          ) : (
                            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <p className="text-sm text-amber-600 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                No payment method on file
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Next Invoice */}
                        <div className="space-y-2" data-testid="billing-upcoming-invoice">
                          <h4 className="text-sm font-medium flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            Next Invoice
                          </h4>
                          {billingData.upcoming_invoice ? (
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="font-medium text-lg">
                                ${billingData.upcoming_invoice.total?.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">{billingData.upcoming_invoice.currency}</span>
                              </p>
                              {billingData.upcoming_invoice.tax > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  Incl. ${billingData.upcoming_invoice.tax?.toFixed(2)} tax
                                </p>
                              )}
                              {billingData.upcoming_invoice.total_discount_amounts > 0 && (
                                <p className="text-xs text-green-600">
                                  ${billingData.upcoming_invoice.total_discount_amounts?.toFixed(2)} discount applied
                                </p>
                              )}
                              {billingData.upcoming_invoice.next_payment_attempt && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Due {new Date(billingData.upcoming_invoice.next_payment_attempt * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                              {!billingData.upcoming_invoice.next_payment_attempt && billingData.upcoming_invoice.period_end && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Period ends {new Date(billingData.upcoming_invoice.period_end * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm text-muted-foreground">No upcoming invoice</p>
                            </div>
                          )}
                        </div>

                        {/* Active Discount */}
                        <div className="space-y-2" data-testid="billing-active-discount">
                          <h4 className="text-sm font-medium flex items-center gap-2">
                            <Percent className="h-4 w-4 text-muted-foreground" />
                            Discount
                          </h4>
                          {(() => {
                            // Show customer-level or first subscription-level discount
                            const disc = billingData.customer_discount
                              || (billingData.subscriptions || []).find(s => s.discount)?.discount;
                            if (disc) {
                              return (
                                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                  <p className="font-medium text-green-700 dark:text-green-400">
                                    {disc.coupon_name || disc.coupon_id}
                                  </p>
                                  <p className="text-sm text-green-600 dark:text-green-500">
                                    {disc.percent_off ? `${disc.percent_off}% off` : disc.amount_off ? `$${disc.amount_off} off` : ''}
                                    {disc.duration === 'forever' ? ' — forever' : disc.duration === 'repeating' && disc.duration_in_months ? ` for ${disc.duration_in_months} months` : disc.duration === 'once' ? ' — one-time' : ''}
                                  </p>
                                  {disc.discount_end && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Expires {new Date(disc.discount_end * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="text-sm text-muted-foreground">No active discount</p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Storage Usage */}
                {storageData && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <HardDrive className="h-5 w-5" />
                            Storage Usage
                          </CardTitle>
                          <CardDescription>Document storage for compliance, dockets, and more</CardDescription>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {storageData.used_formatted || '0 bytes'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            of {storageData.total_limit_formatted || '0 GB'}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Storage Progress Bar */}
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              storageData.is_full 
                                ? 'bg-destructive' 
                                : storageData.is_warning 
                                  ? 'bg-amber-500' 
                                  : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(storageData.usage_percent || 0, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{storageData.usage_percent || 0}% used</span>
                          <span>{storageData.available_formatted || '0 GB'} available</span>
                        </div>
                      </div>

                      {/* Warning Message */}
                      {storageData.is_warning && !storageData.is_full && (
                        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-sm text-amber-600 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            You're running low on storage. Consider adding more to avoid upload issues.
                          </p>
                        </div>
                      )}

                      {/* Full Storage Message */}
                      {storageData.is_full && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                          <p className="text-sm text-destructive flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Storage limit reached! You cannot upload new files until you add more storage.
                          </p>
                        </div>
                      )}

                      {/* Storage Breakdown */}
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Plan Storage ({billingData?.tier_name})</span>
                          <span>{storageData.base_limit_formatted}</span>
                        </div>
                        {(storageData.addon_count || 0) > 0 && (
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Add-on Storage ({storageData.addon_count}× 50GB)</span>
                            <span>{storageData.addon_formatted || '0 bytes'}</span>
                          </div>
                        )}
                      </div>

                      {/* Add Storage Button */}
                      {storageData.addon_available && (
                        <Button 
                          onClick={handlePurchaseStorage}
                          disabled={checkoutLoading}
                          className="w-full mt-4"
                          variant={storageData.is_warning || storageData.is_full ? "default" : "outline"}
                        >
                          {checkoutLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          Add 50GB Storage
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Stripe Subscriptions Detail - Shows actual billing from Stripe */}
                {billingData.subscriptions && billingData.subscriptions.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Subscription Details
                      </CardTitle>
                      <CardDescription>Your active subscriptions from Stripe</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {billingData.subscriptions.map((sub, index) => (
                          <div key={sub.id || index} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Badge variant={sub.status === 'active' ? 'default' : 'secondary'} className={sub.status === 'active' ? 'bg-green-500' : ''}>
                                  {sub.status}
                                </Badge>
                                {sub.cancel_at_period_end && (
                                  <Badge variant="destructive">Cancels at period end</Badge>
                                )}
                              </div>
                              {sub.current_period_end && (
                                <span className="text-sm text-muted-foreground">
                                  Renews: {formatDate(new Date(sub.current_period_end * 1000), form.date_format)}
                                </span>
                              )}
                            </div>
                            
                            {/* Subscription-level discount */}
                            {sub.discount && (
                              <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-md flex items-center gap-2" data-testid={`sub-discount-${index}`}>
                                <Percent className="h-3.5 w-3.5 text-green-600" />
                                <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                                  {sub.discount.coupon_name || sub.discount.coupon_id}
                                  {sub.discount.percent_off ? ` — ${sub.discount.percent_off}% off` : sub.discount.amount_off ? ` — $${sub.discount.amount_off} off` : ''}
                                  {sub.discount.duration === 'forever' ? ' (forever)' : sub.discount.duration === 'repeating' && sub.discount.duration_in_months ? ` (${sub.discount.duration_in_months} months)` : sub.discount.duration === 'once' ? ' (one-time)' : ''}
                                </span>
                              </div>
                            )}
                            
                            {/* Current Items */}
                            <div className="space-y-2">
                              {(sub.items || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-center justify-between py-2 border-b last:border-0">
                                  <div>
                                    <span className="font-medium">{item.product_name || 'Subscription Item'}</span>
                                    {item.quantity > 1 && <span className="text-muted-foreground ml-2">×{item.quantity}</span>}
                                  </div>
                                  <span className="font-mono">
                                    ${item.amount?.toFixed(2) || '0.00'}/{item.interval || 'month'}
                                    {item.quantity > 1 && ` ($${(item.amount * item.quantity)?.toFixed(2) || '0.00'}/${item.interval || 'month'})`}
                                  </span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Subscription Schedule - Shows upcoming price changes */}
                            {sub.schedule && sub.schedule.phases && sub.schedule.phases.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  Billing Schedule
                                </h5>
                                <div className="space-y-2">
                                  {sub.schedule.phases.map((phase, phaseIndex) => {
                                    const isCurrent = sub.schedule.current_phase_index === phaseIndex;
                                    const isPast = sub.schedule.current_phase_index !== null && phaseIndex < sub.schedule.current_phase_index;
                                    const isFuture = sub.schedule.current_phase_index !== null && phaseIndex > sub.schedule.current_phase_index;
                                    
                                    return (
                                      <div 
                                        key={phaseIndex} 
                                        className={`p-3 rounded-lg border ${isCurrent ? 'border-primary bg-primary/5' : isPast ? 'opacity-50' : 'border-dashed'}`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            {isCurrent && <Badge className="bg-primary">Current</Badge>}
                                            {isFuture && <Badge variant="outline">Upcoming</Badge>}
                                            {isPast && <Badge variant="secondary">Completed</Badge>}
                                            <span className="text-sm">
                                              {phase.start_date && formatDate(new Date(phase.start_date * 1000), form.date_format)}
                                              {phase.end_date && ` - ${formatDate(new Date(phase.end_date * 1000), form.date_format)}`}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          {(phase.items || []).map((phaseItem, itemIdx) => (
                                            <div key={itemIdx} className="flex justify-between text-sm">
                                              <span className="text-muted-foreground">{phaseItem.product_name || 'Item'}</span>
                                              <span className="font-mono font-medium">
                                                ${phaseItem.amount?.toFixed(2) || '0.00'}/month
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Manage Extra Resources */}
                <Card className="card-industrial">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <SettingsIcon className="h-5 w-5" />
                      Manage Extra Resources
                    </CardTitle>
                    <CardDescription>Add or remove mobile licences (full &amp; timesheet-only) and users from your subscription</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Extra Operators (Full mobile licences) */}
                      <div className="border rounded-lg p-4 space-y-4" data-testid="billing-full-licence-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Full Mobile Licences</h4>
                            <p className="text-sm text-muted-foreground">${billingData.extra_operator_price}/licence/month</p>
                          </div>
                          <Badge variant="outline" className="text-lg px-3 py-1">
                            {billingData.operators_extra} active
                          </Badge>
                        </div>

                        {billingData.pending_extras_reduction?.extra_operator_full && (
                          <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-sm" data-testid="pending-reduction-full-licence">
                            <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            <span>
                              Reducing to <strong>{billingData.pending_extras_reduction.extra_operator_full.new_quantity}</strong> on{' '}
                              {billingData.pending_extras_reduction.extra_operator_full.effective_at
                                ? new Date(billingData.pending_extras_reduction.extra_operator_full.effective_at).toLocaleDateString()
                                : 'next billing date'}
                            </span>
                          </div>
                        )}
                        
                        {billingData.operators_extra > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                            <span className="text-sm">Adjust quantity:</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', Math.max(0, billingData.operators_extra - 1))}
                              disabled={checkoutLoading || billingData.operators_extra === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-mono">{billingData.operators_extra}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', billingData.operators_extra + 1)}
                              disabled={checkoutLoading}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        
                        <div className="border-t pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Add more operators:</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={extraResourceQuantity}
                              onChange={(e) => setExtraResourceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${(billingData.extra_operator_price * extraResourceQuantity).toFixed(2)}/month
                            </span>
                          </div>
                          <Button 
                            onClick={() => canModifyExtrasInline()
                              ? handleModifyExtras('operator', billingData.operators_extra + extraResourceQuantity)
                              : handlePurchaseExtra('operator', extraResourceQuantity)
                            }
                            disabled={checkoutLoading}
                            className="w-full mt-2"
                            variant="outline"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add {extraResourceQuantity} Licence{extraResourceQuantity > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>

                      {/* Timesheet-Only Licences (Advanced/Professional only) */}
                      {billingData.timesheet_licence_available && (
                      <div className="border rounded-lg p-4 space-y-4" data-testid="billing-timesheet-licence-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Timesheet-Only Licences</h4>
                            <p className="text-sm text-muted-foreground">${billingData.extra_operator_timesheet_price}/licence/month</p>
                          </div>
                          <Badge variant="outline" className="text-lg px-3 py-1" data-testid="timesheet-licence-count">
                            {billingData.operators_timesheet_extra || 0} active
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {billingData.operators_timesheet_used || 0} of {billingData.operators_timesheet_extra || 0} in use. Restricted to timesheets &amp; leave on mobile.
                        </p>

                        {billingData.pending_extras_reduction?.extra_operator_timesheet_timesheet && (
                          <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-sm" data-testid="pending-reduction-timesheet-licence">
                            <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            <span>
                              Reducing to <strong>{billingData.pending_extras_reduction.extra_operator_timesheet_timesheet.new_quantity}</strong> on{' '}
                              {billingData.pending_extras_reduction.extra_operator_timesheet_timesheet.effective_at
                                ? new Date(billingData.pending_extras_reduction.extra_operator_timesheet_timesheet.effective_at).toLocaleDateString()
                                : 'next billing date'}
                            </span>
                          </div>
                        )}

                        {(billingData.operators_timesheet_extra || 0) > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                            <span className="text-sm">Adjust quantity:</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', Math.max(0, (billingData.operators_timesheet_extra || 0) - 1), 'timesheet')}
                              disabled={checkoutLoading || (billingData.operators_timesheet_extra || 0) === 0}
                              data-testid="timesheet-licence-decrement"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-mono">{billingData.operators_timesheet_extra || 0}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', (billingData.operators_timesheet_extra || 0) + 1, 'timesheet')}
                              disabled={checkoutLoading}
                              data-testid="timesheet-licence-increment"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}

                        <div className="border-t pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Add more timesheet licences:</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={extraResourceQuantity}
                              onChange={(e) => setExtraResourceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${((billingData.extra_operator_timesheet_price || 0) * extraResourceQuantity).toFixed(2)}/month
                            </span>
                          </div>
                          <Button 
                            onClick={() => handleModifyExtras('operator', (billingData.operators_timesheet_extra || 0) + extraResourceQuantity, 'timesheet')}
                            disabled={checkoutLoading || !billingData.stripe_customer_id}
                            className="w-full mt-2"
                            variant="outline"
                            data-testid="timesheet-licence-add-btn"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add {extraResourceQuantity} Timesheet Licence{extraResourceQuantity > 1 ? 's' : ''}
                          </Button>
                          {!billingData.stripe_customer_id && (
                            <p className="text-xs text-amber-500 mt-1">Subscribe to a plan first to add licences.</p>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Extra Users */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Extra Users</h4>
                            <p className="text-sm text-muted-foreground">${billingData.extra_user_price}/user/month</p>
                          </div>
                          <Badge variant="outline" className="text-lg px-3 py-1">
                            {billingData.users_extra} active
                          </Badge>
                        </div>

                        {billingData.pending_extras_reduction?.extra_user_full && (
                          <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-sm" data-testid="pending-reduction-users">
                            <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            <span>
                              Reducing to <strong>{billingData.pending_extras_reduction.extra_user_full.new_quantity}</strong> on{' '}
                              {billingData.pending_extras_reduction.extra_user_full.effective_at
                                ? new Date(billingData.pending_extras_reduction.extra_user_full.effective_at).toLocaleDateString()
                                : 'next billing date'}
                            </span>
                          </div>
                        )}
                        
                        {billingData.users_extra > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                            <span className="text-sm">Adjust quantity:</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('user', Math.max(0, billingData.users_extra - 1))}
                              disabled={checkoutLoading || billingData.users_extra === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-mono">{billingData.users_extra}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('user', billingData.users_extra + 1)}
                              disabled={checkoutLoading}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        
                        <div className="border-t pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Add more users:</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={extraResourceQuantity}
                              onChange={(e) => setExtraResourceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${(billingData.extra_user_price * extraResourceQuantity).toFixed(2)}/month
                            </span>
                          </div>
                          <Button 
                            onClick={() => canModifyExtrasInline()
                              ? handleModifyExtras('user', billingData.users_extra + extraResourceQuantity)
                              : handlePurchaseExtra('user', extraResourceQuantity)
                            }
                            disabled={checkoutLoading}
                            className="w-full mt-2"
                            variant="outline"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add {extraResourceQuantity} User{extraResourceQuantity > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Available Plans */}
                {allTiers && billingData && (
                  <Card className="card-industrial" id="change-plan-section">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Change Your Plan
                      </CardTitle>
                      <CardDescription>Upgrade or downgrade your subscription. Extra operators and users will be migrated to the new plan's pricing.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(allTiers).map(([tierId, tierConfig]) => {
                          const isCurrentTier = tierId === (billingData.tier || 'basic');
                          const tierOrder = ['basic', 'advanced', 'professional'];
                          const currentTierIndex = tierOrder.indexOf(billingData.tier || 'basic');
                          const thisTierIndex = tierOrder.indexOf(tierId);
                          const isUpgrade = thisTierIndex > currentTierIndex;
                          const isDowngrade = thisTierIndex < currentTierIndex;
                          
                          return (
                            <div 
                              key={tierId}
                              className={`border rounded-lg p-4 ${
                                isCurrentTier ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-heading font-semibold">{tierConfig.name}</h4>
                                {isCurrentTier && <Badge>Current</Badge>}
                                {isUpgrade && <Badge variant="outline" className="text-green-600 border-green-600">Upgrade</Badge>}
                                {isDowngrade && <Badge variant="outline" className="text-orange-600 border-orange-600">Downgrade</Badge>}
                              </div>
                              <p className="text-2xl font-bold mb-2">${tierConfig.base_price}<span className="text-sm font-normal text-muted-foreground">/month</span></p>
                              <p className="text-sm text-muted-foreground mb-4">{tierConfig.description}</p>
                              
                              <div className="space-y-2 text-sm mb-4">
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.operator_limit} mobile operators included</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.user_limit} user included</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.features?.length || 0} features</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>Extra mobile operator: ${tierConfig.extra_operator_price}/mo</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>Extra user: ${tierConfig.extra_user_price}/mo</span>
                                </div>
                              </div>
                              
                              {isCurrentTier ? (
                                <Button disabled className="w-full" variant="outline">
                                  Current Plan
                                </Button>
                              ) : billingData.pending_tier_change?.new_tier === tierId ? (
                                <Button disabled className="w-full" variant="outline">
                                  <Clock className="h-4 w-4 mr-2" />
                                  Scheduled
                                </Button>
                              ) : billingData.stripe_customer_id ? (
                                <Button 
                                  onClick={() => handleChangePlan(tierId)}
                                  disabled={checkoutLoading}
                                  className="w-full"
                                  variant={isUpgrade ? "default" : "outline"}
                                >
                                  {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : isUpgrade ? <Zap className="h-4 w-4 mr-2" /> : <ArrowDown className="h-4 w-4 mr-2" />}
                                  {isUpgrade ? 'Upgrade' : 'Downgrade'} to {tierConfig.name}
                                </Button>
                              ) : (
                                <Button 
                                  onClick={() => handleSubscriptionUpgrade(tierId)}
                                  disabled={checkoutLoading}
                                  className="w-full"
                                >
                                  {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                                  Subscribe to {tierConfig.name}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* PPD Credits Card */}
                <PPDCreditsCard />

                {/* Cancel Subscription (tenant admin only, active subscription, not already cancelling) */}
                {isAdminUser(user) && billingData.stripe_customer_id && !billingData.subscription_cancelling && (
                  <Card className="card-industrial border-red-500/30" data-testid="cancel-subscription-card">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2 text-red-600">
                        <Ban className="h-5 w-5" />
                        Cancel Subscription
                      </CardTitle>
                      <CardDescription>
                        Cancelling stops your next payment. You'll keep full access until the end of your current billing period, after which your account will be suspended.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-500/10"
                        onClick={() => setCancelDialogOpen(true)}
                        data-testid="open-cancel-subscription-button"
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Cancel my subscription
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Cancel Subscription Dialog */}
                <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                  <DialogContent data-testid="cancel-subscription-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-600">
                        <Ban className="h-5 w-5" />
                        Cancel your subscription
                      </DialogTitle>
                      <DialogDescription>
                        Your subscription will not renew. You'll keep full access until the end of your current billing period, then your account will be suspended. We'd appreciate knowing why you're leaving.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label>Reason for cancelling <span className="text-red-500">*</span></Label>
                        <Select value={cancelReason} onValueChange={setCancelReason}>
                          <SelectTrigger data-testid="cancel-reason-select">
                            <SelectValue placeholder="Select a reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {CANCEL_REASONS.map((r) => (
                              <SelectItem key={r} value={r} data-testid={`cancel-reason-${r}`}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Anything else you'd like to share? (optional)</Label>
                        <Textarea
                          value={cancelComment}
                          onChange={(e) => setCancelComment(e.target.value)}
                          placeholder="Tell us more about why you're cancelling..."
                          rows={3}
                          data-testid="cancel-comment-textarea"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCancelDialogOpen(false)} data-testid="cancel-dialog-keep-button">
                        Keep my subscription
                      </Button>
                      <Button
                        className="bg-red-600 text-white hover:bg-red-700"
                        onClick={handleCancelSubscription}
                        disabled={cancelLoading || !cancelReason}
                        data-testid="confirm-cancel-subscription-button"
                      >
                        {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
                        Confirm cancellation
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Transaction History */}
                {transactions.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Transaction History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.slice(0, 10).map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell>{tx.created_at ? formatDate(tx.created_at, form.date_format) : '-'}</TableCell>
                              <TableCell className="capitalize">{(tx.transaction_type || '').replace(/_/g, ' ') || '-'}</TableCell>
                              <TableCell>${(tx.amount || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <Badge variant={tx.payment_status === 'paid' ? 'default' : tx.payment_status === 'pending' ? 'secondary' : 'destructive'}>
                                  {tx.payment_status || 'unknown'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="card-industrial">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Failed to load billing information</p>
                  <Button onClick={fetchBillingData} variant="outline" className="mt-4">
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Accounting Tab - Xero and MYOB Integration */}
        <TabsContent value="accounting" className="mt-6">
          <Tabs defaultValue="connections" className="w-full">
            <TabsList className="mb-2">
              <TabsTrigger value="connections" data-testid="acct-subtab-connections">Connections & Invoicing</TabsTrigger>
              <TabsTrigger value="payroll" data-testid="acct-subtab-payroll">Payroll</TabsTrigger>
              <TabsTrigger value="payroll-rules" data-testid="acct-subtab-payroll-rules">Payroll Rules</TabsTrigger>
            </TabsList>
            <TabsContent value="connections" className="mt-6">
          <div className="grid gap-6" data-testid="accounting-integration">
            {/* Connected Accounting Systems (multi-connection) */}
            {acctConnections.length > 0 && (
              <Card className="card-industrial" data-testid="accounting-connections-list">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Connected Accounting Systems</CardTitle>
                  <CardDescription>
                    You can connect multiple systems (including more than one MYOB company file). New customers export to their assigned connection; the default is used when none is set.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {acctConnections.map((conn) => (
                    <div key={conn.id} className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg border border-border bg-muted/30" data-testid={`connection-row-${conn.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-mono uppercase px-2 py-1 rounded bg-muted text-muted-foreground">{conn.provider}</span>
                        {renamingConnId === conn.id ? (
                          <span className="flex items-center gap-2">
                            <Input value={renameLabel} onChange={(e) => setRenameLabel(e.target.value)} className="h-8 w-48" data-testid={`rename-input-${conn.id}`} />
                            <Button size="sm" onClick={() => handleRenameConnection(conn.id)} data-testid={`rename-save-${conn.id}`}>Save</Button>
                          </span>
                        ) : (
                          <div className="min-w-0">
                            <p className="font-medium truncate">{conn.label}</p>
                            {conn.company_name && conn.company_name !== conn.label && (
                              <p className="text-xs text-muted-foreground truncate">{conn.company_name}</p>
                            )}
                          </div>
                        )}
                        {conn.is_default && (
                          <Badge className="bg-[#ffd108] text-black" data-testid={`default-badge-${conn.id}`}>Default</Badge>
                        )}
                        {conn.status === 'needs_reauth' && (
                          <Badge variant="destructive" data-testid={`reauth-badge-${conn.id}`}>Needs reconnect</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!conn.is_default && (
                          <Button variant="outline" size="sm" onClick={() => handleSetDefaultConnection(conn.id)} data-testid={`set-default-${conn.id}`}>Set default</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setRenamingConnId(conn.id); setRenameLabel(conn.label); }} data-testid={`rename-${conn.id}`}>Rename</Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-500/10" onClick={() => handleDisconnectConnection(conn.id)} data-testid={`disconnect-${conn.id}`}>Disconnect</Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Xero Connection Card */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Xero Integration
                </CardTitle>
                <CardDescription>Connect your Xero account to push approved dockets as invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {xeroLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : xeroStatus.connected ? (
                  <div className="space-y-4">
                    {/* Connected Status */}
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800 dark:text-green-200">Connected to Xero</p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Organization: <span className="font-semibold">{xeroStatus.tenant_name}</span>
                        </p>
                        {xeroStatus.connected_at && (
                          <p className="text-xs text-green-500 dark:text-green-500 mt-1">
                            Connected on {formatDate(xeroStatus.connected_at, form.date_format)}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestAccountingConnection('xero')}
                        disabled={testingConnection.xero}
                        data-testid="test-connection-xero-btn"
                      >
                        {testingConnection.xero ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Testing...</>
                        ) : (
                          <><Activity className="h-4 w-4 mr-2" /> Test Connection</>
                        )}
                      </Button>
                      {isAdminUser(user) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                              <Unlink className="h-4 w-4 mr-2" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect Xero?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will disconnect your Xero account. You won't be able to push dockets to Xero until you reconnect.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={handleDisconnectXero} className="bg-red-600 hover:bg-red-700">
                                Disconnect
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    <TestConnectionResult result={testConnectionResult.xero} />

                    {/* Connect Another Xero */}
                    {isAdminUser(user) && (
                      <div className="flex items-center gap-3 p-3 border border-dashed border-[#13B5EA]/40 rounded-lg bg-[#13B5EA]/5">
                        <div className="flex-1">
                          <p className="text-sm font-medium">Connect another Xero organisation</p>
                          <p className="text-xs text-muted-foreground">Add a second Xero connection to route invoices to different organisations</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleConnectXero}
                          disabled={xeroConnecting}
                          data-testid="connect-another-xero-btn"
                        >
                          {xeroConnecting ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Connecting...</>
                          ) : (
                            <><Plus className="h-4 w-4 mr-1" /> Connect Another</>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Per-connection Account & Item cache sync + Customer Import */}
                    {acctConnections.filter(c => c.provider === 'xero').length > 1 ? (
                      acctConnections.filter(c => c.provider === 'xero').map(conn => (
                        <div key={conn.id} className="border rounded-lg p-3 space-y-3" data-testid={`xero-connection-ops-${conn.id}`}>
                          <p className="text-sm font-medium text-[#13B5EA]">{conn.label || conn.company_name}</p>
                          <AccountSyncRow platform="xero" state={accountSyncState[_syncKey('xero', conn.id)]} onSync={handleSyncAccounts} connectionId={conn.id} dataTestId={`account-sync-row-xero-${conn.id}`} />
                          <div className="border rounded-lg p-3 bg-muted/30">
                            <div className="flex items-center justify-between">
                              <div><h4 className="text-sm font-medium flex items-center gap-2"><UsersIcon className="h-4 w-4 text-muted-foreground" />Customer Sync</h4><p className="text-xs text-muted-foreground mt-0.5">Import customers from this Xero organisation</p></div>
                              <Button onClick={() => handleImportCustomersFromXero(conn.id)} disabled={!!importingConn[`xero:${conn.id}`]} variant="outline" size="sm" data-testid={`import-customers-xero-${conn.id}`}>
                                {importingConn[`xero:${conn.id}`] ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><ExternalLink className="h-4 w-4 mr-2" /> Import</>}
                              </Button>
                            </div>
                            {importResults[`xero:${conn.id}`] && (
                              <div className="mt-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2 text-xs">
                                <span className="text-green-600">New: <b>{importResults[`xero:${conn.id}`].imported}</b></span>{' · '}<span className="text-green-600">Linked: <b>{importResults[`xero:${conn.id}`].linked}</b></span>
                                {importResults[`xero:${conn.id}`].archived > 0 && <>{' · '}<span className="text-amber-600">Archived: <b>{importResults[`xero:${conn.id}`].archived}</b></span></>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <>
                        {/* Single connection — legacy layout */}
                        <AccountSyncRow
                          platform="xero"
                          state={accountSyncState[_syncKey('xero', acctConnections.find(c => c.provider === 'xero')?.id)] || accountSyncState.xero}
                          onSync={handleSyncAccounts}
                          connectionId={acctConnections.find(c => c.provider === 'xero')?.id}
                        />

                    {/* Customer Sync Section */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <UsersIcon className="h-4 w-4 text-muted-foreground" />
                            Customer Sync
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Import customers from Xero to link with EQUIPR CORE.
                          </p>
                        </div>
                        <Button
                          onClick={() => handleImportCustomersFromXero(acctConnections.find(c => c.provider === 'xero')?.id)}
                          disabled={xeroImporting}
                          variant="outline"
                          size="sm"
                        >
                          {xeroImporting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Import from Xero
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {xeroImportResult && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3" data-testid="xero-import-result">
                          <p className="text-sm text-green-800 dark:text-green-200 font-medium">Sync Complete</p>
                          <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-green-600">New:</span> <span className="font-semibold">{xeroImportResult.imported}</span>
                            </div>
                            <div>
                              <span className="text-green-600">Linked:</span> <span className="font-semibold">{xeroImportResult.linked}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Skipped:</span> <span className="font-semibold">{xeroImportResult.skipped}</span>
                            </div>
                            <div>
                              <span className="text-amber-600">Archived:</span> <span className="font-semibold" data-testid="xero-archived-count">{xeroImportResult.archived || 0}</span>
                            </div>
                          </div>
                          {xeroImportResult.archived > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                              Archived {xeroImportResult.archived} customer(s) inactive in Xero: {(xeroImportResult.archived_customers || []).map(c => c.company_name).filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-muted/50 rounded-lg">
                    <div className="w-12 h-12 mx-auto mb-3 bg-[#13B5EA]/10 rounded-full flex items-center justify-center">
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="font-heading font-semibold mb-2">Connect Xero</h3>
                    <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                      Push approved dockets as draft invoices to Xero.
                    </p>
                    {isAdminUser(user) ? (
                      <Button 
                        onClick={handleConnectXero}
                        disabled={xeroConnecting}
                        className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
                      >
                        {xeroConnecting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Link className="h-4 w-4 mr-2" />
                            Connect to Xero
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-sm text-amber-600">Admin required to connect</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* MYOB Connection Card */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                    <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  MYOB Integration
                  {myobStatus.connected && (
                    <Badge className="bg-green-500/20 text-green-600 ml-2">Connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>Connect your MYOB Business account to push approved dockets as invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {myobLoading ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                  </div>
                ) : myobStatus.connected ? (
                  <div className="space-y-4">
                    {/* Per-connection operational sections */}
                    {acctConnections.filter(c => c.provider === 'myob').map((conn) => {
                      const connKey = conn.id;
                      const syncState = accountSyncState[_syncKey('myob', connKey)];
                      const connImporting = importingConn[`myob:${connKey}`];
                      const connImportResult = importResults[`myob:${connKey}`];
                      const needsReauth = conn.status === 'needs_reauth';
                      return (
                        <div key={conn.id} className="border rounded-lg overflow-hidden" data-testid={`myob-connection-panel-${conn.id}`}>
                          {/* Connection header */}
                          <div className="flex items-center justify-between gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                                  <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{conn.label || conn.company_name || 'MYOB Company'}</p>
                                {conn.connected_at && (
                                  <p className="text-xs text-muted-foreground">Connected {formatDate(conn.connected_at, form.date_format)}</p>
                                )}
                              </div>
                              {conn.is_default && <Badge className="bg-[#ffd108] text-black text-xs">Default</Badge>}
                              {conn.product_type === 'accountright' && <Badge variant="outline" className="text-xs">AccountRight</Badge>}
                              {conn.product_type === 'business' && <Badge variant="outline" className="text-xs">MYOB Business</Badge>}
                              {needsReauth && <Badge variant="destructive" className="text-xs">Needs reconnect</Badge>}
                            </div>
                            <Button variant="outline" size="sm" onClick={() => handleTestAccountingConnection('myob')} disabled={testingConnection.myob} data-testid={`test-connection-myob-${conn.id}`}>
                              {testingConnection.myob ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Testing...</> : <><Activity className="h-4 w-4 mr-1" /> Test</>}
                            </Button>
                          </div>
                          {needsReauth && (
                            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                <p className="text-sm text-amber-700 dark:text-amber-300 flex-1">{conn.reauth_reason || 'Authentication expired. Please reconnect.'}</p>
                                {isAdminUser(user) && <Button size="sm" onClick={handleConnectMyob} disabled={myobConnecting} className="bg-amber-600 hover:bg-amber-700">{myobConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reconnect'}</Button>}
                              </div>
                            </div>
                          )}
                          <div className="p-4 space-y-3">
                            <AccountSyncRow platform="myob" state={syncState} onSync={handleSyncAccounts} connectionId={connKey} dataTestId={`account-sync-row-myob-${conn.id}`} />
                            {conn.product_type === 'accountright' && isAdminUser(user) && (
                              <div className="border rounded-lg p-3 bg-muted/30">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="text-sm font-medium flex items-center gap-2"><KeyRound className="h-4 w-4 text-muted-foreground" />Company File Credentials</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">{conn.has_cf_credentials ? 'Credentials configured' : 'No credentials — import may fail'}</p>
                                  </div>
                                  {conn.has_cf_credentials ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 text-xs"><Check className="h-3 w-3 mr-1" /> Configured</Badge> : <Badge variant="destructive" className="text-xs"><AlertCircle className="h-3 w-3 mr-1" /> Missing</Badge>}
                                </div>
                                <div className="mt-2">
                                  <Dialog>
                                    <DialogTrigger asChild><Button variant="outline" size="sm"><Edit className="h-4 w-4 mr-2" />{conn.has_cf_credentials ? 'Update' : 'Add'} Credentials</Button></DialogTrigger>
                                    <DialogContent>
                                      <DialogHeader><DialogTitle>MYOB AccountRight Credentials — {conn.label}</DialogTitle><DialogDescription>Enter the username and password for this MYOB company file.</DialogDescription></DialogHeader>
                                      <div className="space-y-4 py-4">
                                        <div><Label>Company File Username</Label><Input value={myobSettings.cf_username} onChange={(e) => setMyobSettings(s => ({ ...s, cf_username: e.target.value }))} placeholder="e.g., Administrator" className="mt-1" /></div>
                                        <div><Label>Company File Password</Label><Input type="password" value={myobSettings.cf_password} onChange={(e) => setMyobSettings(s => ({ ...s, cf_password: e.target.value }))} placeholder="Enter password" className="mt-1" /></div>
                                      </div>
                                      <DialogFooter><Button onClick={async () => { try { await api.post('/myob/settings', { product_type: 'accountright', cf_username: myobSettings.cf_username, cf_password: myobSettings.cf_password }, { params: { connection_id: conn.id } }); toast.success('Credentials updated'); fetchAcctConnections(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update credentials'); } }} disabled={!myobSettings.cf_username || !myobSettings.cf_password}>Save Credentials</Button></DialogFooter>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              </div>
                            )}
                            <div className="border rounded-lg p-3 bg-muted/30">
                              <div className="flex items-center justify-between">
                                <div><h4 className="text-sm font-medium flex items-center gap-2"><UsersIcon className="h-4 w-4 text-muted-foreground" />Customer Sync</h4><p className="text-xs text-muted-foreground mt-0.5">Import customers from this MYOB company file</p></div>
                                <Button onClick={() => handleImportCustomersFromMyob(conn.id)} disabled={!!connImporting} variant="outline" size="sm" data-testid={`import-customers-myob-${conn.id}`}>
                                  {connImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><ExternalLink className="h-4 w-4 mr-2" /> Import</>}
                                </Button>
                              </div>
                              {connImportResult && (
                                <div className="mt-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2 text-xs">
                                  <span className="text-green-600">New: <b>{connImportResult.imported}</b></span>{' · '}<span className="text-green-600">Updated: <b>{connImportResult.updated}</b></span>
                                  {connImportResult.removed > 0 && <>{' · '}<span className="text-red-600">Removed: <b>{connImportResult.removed}</b></span></>}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Connect Another MYOB */}
                    {isAdminUser(user) && (
                      <div className="flex items-center gap-3 p-3 border border-dashed border-purple-300 dark:border-purple-700 rounded-lg bg-purple-50/50 dark:bg-purple-900/10">
                        <div className="flex-1"><p className="text-sm font-medium">Connect another MYOB company file</p><p className="text-xs text-muted-foreground">Add a second MYOB connection to route invoices to different company files</p></div>
                        <Dialog open={showAddMyobDialog} onOpenChange={(open) => { setShowAddMyobDialog(open); if (!open) setAddMyobSettings({ product_type: '', cf_username: '', cf_password: '' }); }}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="connect-another-myob-btn">
                              <Plus className="h-4 w-4 mr-1" /> Connect Another
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Connect Another MYOB Account</DialogTitle>
                              <DialogDescription>Select the product type for this MYOB company file. If it is AccountRight, enter the company file credentials.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-2">
                              <div className="space-y-3">
                                <label className="text-sm font-medium">Which MYOB product?</label>
                                <div className="grid gap-3">
                                  <div
                                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${addMyobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                                    onClick={() => setAddMyobSettings(s => ({ ...s, product_type: 'business' }))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-4 h-4 rounded-full border-2 ${addMyobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                        {addMyobSettings.product_type === 'business' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                      </div>
                                      <div><p className="font-medium text-sm">MYOB Business</p><p className="text-xs text-muted-foreground">Cloud-based (formerly Essentials)</p></div>
                                    </div>
                                  </div>
                                  <div
                                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${addMyobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                                    onClick={() => setAddMyobSettings(s => ({ ...s, product_type: 'accountright' }))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-4 h-4 rounded-full border-2 ${addMyobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                        {addMyobSettings.product_type === 'accountright' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                      </div>
                                      <div><p className="font-medium text-sm">MYOB AccountRight</p><p className="text-xs text-muted-foreground">Desktop/hosted with API access</p></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {addMyobSettings.product_type === 'accountright' && (
                                <div className="space-y-3 border-t pt-3">
                                  <p className="text-sm font-medium">Company File Credentials</p>
                                  <div><Label>Username</Label><Input value={addMyobSettings.cf_username} onChange={(e) => setAddMyobSettings(s => ({ ...s, cf_username: e.target.value }))} placeholder="e.g., Administrator" className="mt-1" /></div>
                                  <div><Label>Password</Label><Input type="password" value={addMyobSettings.cf_password} onChange={(e) => setAddMyobSettings(s => ({ ...s, cf_password: e.target.value }))} placeholder="Company file password" className="mt-1" /></div>
                                </div>
                              )}
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setShowAddMyobDialog(false)}>Cancel</Button>
                              <Button onClick={handleAddMyobConnection} disabled={myobConnecting || !addMyobSettings.product_type} className="bg-purple-600 hover:bg-purple-700">
                                {myobConnecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</> : <><Link className="h-4 w-4 mr-2" /> Connect to MYOB</>}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-muted/50 rounded-lg">
                    {!showMyobSetup ? (
                      <>
                        <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                            <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </div>
                        <h3 className="font-heading font-semibold mb-2">Connect MYOB</h3>
                        <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                          Push approved dockets as draft invoices to MYOB Business or AccountRight.
                        </p>
                        {isAdminUser(user) ? (
                          <Button 
                            onClick={handleStartMyobSetup}
                            disabled={myobConnecting}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Link className="h-4 w-4 mr-2" />
                            Connect to MYOB
                          </Button>
                        ) : (
                          <p className="text-sm text-amber-600">Admin required to connect</p>
                        )}
                      </>
                    ) : (
                      <div className="text-left max-w-md mx-auto space-y-6">
                        <div>
                          <h3 className="font-heading font-semibold mb-1">MYOB Product Setup</h3>
                          <p className="text-muted-foreground text-sm">
                            Select your MYOB product and configure access settings.
                          </p>
                        </div>

                        {/* Product Type Selection */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium">Which MYOB product do you use?</label>
                          <div className="grid gap-3">
                            <div 
                              className={`p-4 border rounded-lg cursor-pointer transition-colors ${myobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                              onClick={() => setMyobSettings(s => ({ ...s, product_type: 'business' }))}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 ${myobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                  {myobSettings.product_type === 'business' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                </div>
                                <div>
                                  <p className="font-medium">MYOB Business</p>
                                  <p className="text-xs text-muted-foreground">Cloud-based accounting (formerly Essentials)</p>
                                </div>
                              </div>
                            </div>
                            <div 
                              className={`p-4 border rounded-lg cursor-pointer transition-colors ${myobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                              onClick={() => setMyobSettings(s => ({ ...s, product_type: 'accountright' }))}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 ${myobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                  {myobSettings.product_type === 'accountright' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                </div>
                                <div>
                                  <p className="font-medium">MYOB AccountRight</p>
                                  <p className="text-xs text-muted-foreground">Desktop/hosted accounting software</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* AccountRight Credentials */}
                        {myobSettings.product_type === 'accountright' && (
                          <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-amber-800 dark:text-amber-200 text-sm">Company File Credentials Required</p>
                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                  AccountRight requires a company file user for API access. This is different from your my.myob.com login.
                                </p>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium block mb-1">Company File Username</label>
                                <Input 
                                  value={myobSettings.cf_username}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_username: e.target.value }))}
                                  placeholder="e.g., Administrator"
                                  className="bg-white dark:bg-gray-900"
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium block mb-1">Company File Password</label>
                                <Input 
                                  type="password"
                                  value={myobSettings.cf_password}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_password: e.target.value }))}
                                  placeholder="Enter password"
                                  className="bg-white dark:bg-gray-900"
                                />
                              </div>
                            </div>

                            <details className="text-xs">
                              <summary className="cursor-pointer text-amber-700 dark:text-amber-300 hover:underline">
                                How do I find my company file credentials?
                              </summary>
                              <div className="mt-2 p-3 bg-white dark:bg-gray-900 rounded border text-muted-foreground space-y-2">
                                <p><strong>Company file users</strong> are set up within AccountRight itself:</p>
                                <ol className="list-decimal list-inside space-y-1 ml-2">
                                  <li>Open your company file in MYOB AccountRight</li>
                                  <li>Go to <strong>Setup → Preferences → Security</strong></li>
                                  <li>Create or use an existing user with API access</li>
                                  <li>The default is often "Administrator" with a blank password</li>
                                </ol>
                                <p className="mt-2 text-amber-600">Note: We recommend creating a dedicated API user for security.</p>
                              </div>
                            </details>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                          <Button 
                            variant="outline"
                            onClick={() => setShowMyobSetup(false)}
                            disabled={myobSettingsSaving}
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSaveMyobSettingsAndConnect}
                            disabled={myobSettingsSaving || !myobSettings.product_type || (myobSettings.product_type === 'accountright' && (!myobSettings.cf_username || !myobSettings.cf_password))}
                            className="bg-purple-600 hover:bg-purple-700 flex-1"
                          >
                            {myobSettingsSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <Link className="h-4 w-4 mr-2" />
                                Connect to MYOB
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QuickBooks Online Card */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#2CA01C" strokeWidth="2"/>
                    <path d="M15.5 8.5C15.5 7.12 14.38 6 13 6H10v12h2v-4h1c1.38 0 2.5-1.12 2.5-2.5v-3z" stroke="#2CA01C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 10h1c.55 0 1-.45 1-1s-.45-1-1-1h-1v2z" fill="#2CA01C"/>
                  </svg>
                  QuickBooks Online Integration
                  {qboStatus.connected && (
                    <Badge className="bg-green-500/20 text-green-600 ml-2">Connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>Connect your QuickBooks Online account to push approved dockets as invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {qboLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : qboStatus.connected ? (
                  <div className="space-y-4">
                    {/* Connected Status */}
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800 dark:text-green-200">Connected to QuickBooks</p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Company: <span className="font-semibold">{qboStatus.company_name}</span>
                        </p>
                        {qboStatus.connected_at && (
                          <p className="text-xs text-green-500 dark:text-green-500 mt-1">
                            Connected on {formatDate(qboStatus.connected_at, form.date_format)}
                          </p>
                        )}
                      </div>
                      {isAdminUser(user) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="qbo-disconnect-btn">
                              <Unlink className="h-4 w-4 mr-2" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect QuickBooks?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will disconnect your QuickBooks account. You won't be able to push dockets to QuickBooks until you reconnect.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={handleDisconnectQbo} className="bg-red-600 hover:bg-red-700">
                                Disconnect
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestAccountingConnection('quickbooks')}
                        disabled={testingConnection.quickbooks}
                        data-testid="test-connection-quickbooks-btn"
                      >
                        {testingConnection.quickbooks ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Testing...</>
                        ) : (
                          <><Activity className="h-4 w-4 mr-2" /> Test Connection</>
                        )}
                      </Button>
                    </div>
                    <TestConnectionResult result={testConnectionResult.quickbooks} />

                    {/* Connect Another QuickBooks */}
                    {isAdminUser(user) && (
                      <div className="flex items-center gap-3 p-3 border border-dashed border-[#2CA01C]/40 rounded-lg bg-[#2CA01C]/5">
                        <div className="flex-1">
                          <p className="text-sm font-medium">Connect another QuickBooks company</p>
                          <p className="text-xs text-muted-foreground">Add a second QuickBooks connection to route invoices to different companies</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleConnectQbo}
                          disabled={qboConnecting}
                          data-testid="connect-another-qbo-btn"
                        >
                          {qboConnecting ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Connecting...</>
                          ) : (
                            <><Plus className="h-4 w-4 mr-1" /> Connect Another</>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Per-connection Account & Item cache sync + Customer Import */}
                    {acctConnections.filter(c => c.provider === 'quickbooks').length > 1 ? (
                      acctConnections.filter(c => c.provider === 'quickbooks').map(conn => (
                        <div key={conn.id} className="border rounded-lg p-3 space-y-3" data-testid={`qbo-connection-ops-${conn.id}`}>
                          <p className="text-sm font-medium text-[#2CA01C]">{conn.label || conn.company_name}</p>
                          <AccountSyncRow platform="quickbooks" state={accountSyncState[_syncKey('quickbooks', conn.id)]} onSync={handleSyncAccounts} connectionId={conn.id} dataTestId={`account-sync-row-quickbooks-${conn.id}`} />
                          <div className="border rounded-lg p-3 bg-muted/30">
                            <div className="flex items-center justify-between">
                              <div><h4 className="text-sm font-medium flex items-center gap-2"><UsersIcon className="h-4 w-4 text-muted-foreground" />Customer Sync</h4><p className="text-xs text-muted-foreground mt-0.5">Import customers from this QuickBooks company</p></div>
                              <Button onClick={() => handleImportCustomersFromQbo(conn.id)} disabled={!!importingConn[`quickbooks:${conn.id}`]} variant="outline" size="sm" data-testid={`import-customers-qbo-${conn.id}`}>
                                {importingConn[`quickbooks:${conn.id}`] ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><ExternalLink className="h-4 w-4 mr-2" /> Import</>}
                              </Button>
                            </div>
                            {importResults[`quickbooks:${conn.id}`] && (
                              <div className="mt-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2 text-xs">
                                <span className="text-green-600">New: <b>{importResults[`quickbooks:${conn.id}`].imported}</b></span>{' · '}<span className="text-green-600">Linked: <b>{importResults[`quickbooks:${conn.id}`].linked}</b></span>
                                {importResults[`quickbooks:${conn.id}`].archived > 0 && <>{' · '}<span className="text-amber-600">Archived: <b>{importResults[`quickbooks:${conn.id}`].archived}</b></span></>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <>
                    {/* Account / Item cache sync */}
                    <AccountSyncRow
                      platform="quickbooks"
                      state={accountSyncState[_syncKey('quickbooks', acctConnections.find(c => c.provider === 'quickbooks')?.id)] || accountSyncState.quickbooks}
                      onSync={handleSyncAccounts}
                      connectionId={acctConnections.find(c => c.provider === 'quickbooks')?.id}
                    />

                    {/* Customer Sync Section */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <UsersIcon className="h-4 w-4 text-muted-foreground" />
                            Customer Sync
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Import customers from QuickBooks to link with EQUIPR CORE.
                          </p>
                        </div>
                        <Button
                          onClick={() => handleImportCustomersFromQbo(acctConnections.find(c => c.provider === 'quickbooks')?.id)}
                          disabled={qboImporting}
                          variant="outline"
                          size="sm"
                          data-testid="qbo-import-customers-btn"
                        >
                          {qboImporting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Import from QuickBooks
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {qboImportResult && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3" data-testid="qbo-import-result">
                          <p className="text-sm text-green-800 dark:text-green-200 font-medium">Sync Complete</p>
                          <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-green-600">New:</span> <span className="font-semibold">{qboImportResult.imported}</span>
                            </div>
                            <div>
                              <span className="text-green-600">Linked:</span> <span className="font-semibold">{qboImportResult.linked}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Skipped:</span> <span className="font-semibold">{qboImportResult.skipped}</span>
                            </div>
                            <div>
                              <span className="text-amber-600">Archived:</span> <span className="font-semibold" data-testid="qbo-archived-count">{qboImportResult.archived || 0}</span>
                            </div>
                          </div>
                          {qboImportResult.archived > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                              Archived {qboImportResult.archived} customer(s) inactive in QuickBooks: {(qboImportResult.archived_customers || []).map(c => c.company_name).filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center p-6 bg-muted/50 rounded-lg">
                      <div className="w-12 h-12 mx-auto mb-3 bg-[#2CA01C]/10 rounded-full flex items-center justify-center">
                        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="#2CA01C" strokeWidth="2"/>
                          <path d="M15.5 8.5C15.5 7.12 14.38 6 13 6H10v12h2v-4h1c1.38 0 2.5-1.12 2.5-2.5v-3z" stroke="#2CA01C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 10h1c.55 0 1-.45 1-1s-.45-1-1-1h-1v2z" fill="#2CA01C"/>
                        </svg>
                      </div>
                      <h3 className="font-heading font-semibold mb-2">QuickBooks Online</h3>
                      <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-4">
                        Connect your QuickBooks Online account to push approved dockets as draft invoices and sync customers.
                      </p>
                      <Button
                        onClick={handleConnectQbo}
                        className="bg-[#2CA01C] hover:bg-[#228B1A] text-white"
                        data-testid="qbo-connect-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Connect to QuickBooks
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoice Numbering Setting - Show when any accounting package is connected */}
            {(xeroStatus.connected || myobStatus.connected || qboStatus.connected) && (
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    Invoice Numbering
                  </CardTitle>
                  <CardDescription>Choose how invoice numbers are assigned when exporting dockets</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${invoiceNumberingSource === 'equipr' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveInvoiceNumberingSetting('equipr')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${invoiceNumberingSource === 'equipr' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {invoiceNumberingSource === 'equipr' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">EQUIPR CORE Assigned</p>
                        <p className="text-xs text-muted-foreground">Invoice numbers match Job/Docket numbers (e.g., Job #14 → Docket #14 → INV-00014)</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${invoiceNumberingSource === 'accounting_package' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveInvoiceNumberingSetting('accounting_package')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${invoiceNumberingSource === 'accounting_package' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {invoiceNumberingSource === 'accounting_package' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{connectedAccountingPackage ? connectedAccountingPackage.toUpperCase() : 'Accounting Package'} Assigned</p>
                        <p className="text-xs text-muted-foreground">Let {connectedAccountingPackage?.toUpperCase() || 'your accounting software'} assign invoice numbers automatically</p>
                      </div>
                    </div>
                  </div>
                  
                  {savingInvoiceSetting && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Xero line item code mode — Account Code vs Item Code (Allocate migrants) */}
            {xeroStatus.connected && (
              <Card className="card-industrial" data-testid="xero-line-code-mode-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    Xero Line Item Code
                  </CardTitle>
                  <CardDescription>
                    Choose which Xero field the pricelist code is sent to on each invoice line.
                    Use <span className="font-medium">Item Code</span> if your codes are Xero inventory
                    Items (common when migrating from Allocate); use <span className="font-medium">Account Code</span> for GL account codes.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${xeroLineCodeMode === 'account' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveXeroLineCodeMode('account')}
                      data-testid="xero-line-code-mode-account"
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${xeroLineCodeMode === 'account' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {xeroLineCodeMode === 'account' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Account Code (default)</p>
                        <p className="text-xs text-muted-foreground">Pricelist codes are sent to Xero's AccountCode (GL account) field.</p>
                      </div>
                    </div>

                    <div
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${xeroLineCodeMode === 'item' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveXeroLineCodeMode('item')}
                      data-testid="xero-line-code-mode-item"
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${xeroLineCodeMode === 'item' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {xeroLineCodeMode === 'item' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Item Code</p>
                        <p className="text-xs text-muted-foreground">Pricelist codes are sent to Xero's ItemCode (inventory item) field. Lines with no code fall back to the default account code.</p>
                      </div>
                    </div>
                  </div>

                  {savingXeroLineCodeMode && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* MYOB line code mode — Item Number vs GL Account Code */}
            {myobStatus.connected && (
              <Card className="card-industrial" data-testid="myob-line-code-mode-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    MYOB Line Code
                  </CardTitle>
                  <CardDescription>
                    Choose what the pricelist Account ID means when invoices are exported to MYOB.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${myobLineCodeMode === 'item' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveMyobLineCodeMode('item')}
                      data-testid="myob-line-code-mode-item"
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${myobLineCodeMode === 'item' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {myobLineCodeMode === 'item' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Item Number (default)</p>
                        <p className="text-xs text-muted-foreground">Account IDs are MYOB Item Numbers. Invoices use MYOB's Item layout, with quantity and unit price on each line.</p>
                      </div>
                    </div>

                    <div
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${myobLineCodeMode === 'account' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveMyobLineCodeMode('account')}
                      data-testid="myob-line-code-mode-account"
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${myobLineCodeMode === 'account' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {myobLineCodeMode === 'account' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">GL Account Code</p>
                        <p className="text-xs text-muted-foreground">Account IDs are MYOB account numbers (e.g. 4-1000). Invoices use MYOB's Service layout; each line shows quantity and rate in its description.</p>
                      </div>
                    </div>
                  </div>

                  {savingMyobLineCodeMode && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Invoice Reference Template - Show when any accounting package is connected */}
            {(xeroStatus.connected || myobStatus.connected || qboStatus.connected) && (
              <Card className="card-industrial" data-testid="invoice-reference-template-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    Invoice Reference Template
                  </CardTitle>
                  <CardDescription>
                    Build the reference/PO text sent to your accounting package using tags and free text.
                    Leave blank to use the default (PO number + Job/Docket).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm">Template</Label>
                    <Input
                      data-testid="invoice-reference-template-input"
                      value={invoiceReferenceTemplate}
                      onChange={(e) => setInvoiceReferenceTemplate(e.target.value)}
                      placeholder="e.g. PO {po_number} - Job #{job_number} ({job_date})"
                      className="mt-1 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Insert a tag</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {REFERENCE_TAGS.map((t) => (
                        <Badge
                          key={t.tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-muted transition-colors"
                          data-testid={`reference-tag-${t.tag.replace(/[{}]/g, '')}`}
                          onClick={() => setInvoiceReferenceTemplate((prev) => `${prev || ''}${t.tag}`)}
                          title={t.label}
                        >
                          {t.tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground mb-1">Preview (sample data)</p>
                    <p className="text-sm font-mono break-words" data-testid="invoice-reference-preview">
                      {renderReferencePreview(invoiceReferenceTemplate) || <span className="text-muted-foreground italic">Default reference will be used</span>}
                    </p>
                  </div>

                  {myobStatus.connected && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="myob-reference-warning">
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        <strong>MYOB note:</strong> MYOB's Purchase Order field only stores 20 characters — longer references
                        are truncated. Keep your MYOB template short.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleSaveReferenceTemplate}
                      disabled={savingReferenceTemplate}
                      className="btn-volt"
                      data-testid="save-invoice-reference-template-btn"
                    >
                      {savingReferenceTemplate ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                      ) : 'Save Template'}
                    </Button>
                    {invoiceReferenceTemplate && (
                      <Button
                        variant="ghost"
                        onClick={() => setInvoiceReferenceTemplate('')}
                        data-testid="clear-invoice-reference-template-btn"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invoice Email Template - shown when an accounting package is connected */}
            {(xeroStatus.connected || myobStatus.connected || connectedAccountingPackage) && (
              <Card className="card-industrial" data-testid="invoice-email-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5 text-teal-600" />
                    Invoice Email
                  </CardTitle>
                  <CardDescription>
                    The email used when sending an invoice to a customer from a docket. Available tags:
                    <span className="font-mono text-xs"> {'{customer_name}'} {'{invoice_number}'} {'{invoice_date}'} {'{total_amount}'} {'{company_name}'}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Subject</label>
                    <Input
                      value={invoiceEmailSubject}
                      onChange={(e) => setInvoiceEmailSubject(e.target.value)}
                      placeholder={DEFAULT_INVOICE_EMAIL_SUBJECT}
                      data-testid="invoice-email-subject-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Body</label>
                    <Textarea
                      rows={8}
                      value={invoiceEmailBody}
                      onChange={(e) => setInvoiceEmailBody(e.target.value)}
                      placeholder={DEFAULT_INVOICE_EMAIL_BODY}
                      data-testid="invoice-email-body-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveInvoiceEmail} disabled={savingInvoiceEmail} data-testid="save-invoice-email-btn">
                      {savingInvoiceEmail ? 'Saving…' : 'Save Template'}
                    </Button>
                    <Button variant="outline" onClick={handleResetInvoiceEmail} data-testid="reset-invoice-email-btn">
                      Reset to default
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* How it works - when no accounting package connected */}
            {!xeroStatus.connected && !myobStatus.connected && (
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-muted-foreground" />
                    How it works
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Approved dockets can be pushed to your accounting software as Draft invoices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Invoice will include customer, job reference, and docket items</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Review and send invoices from within Xero or MYOB</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Secure OAuth 2.0 authentication - we never see your password</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Export Failures - shared across all accounting integrations */}
            <Card className="card-industrial" data-testid="export-failures-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    Recent Export Failures
                  </CardTitle>
                  <CardDescription>
                    The last 50 failed invoice exports across Xero, MYOB, and QuickBooks. Use these to diagnose recurring issues.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchExportFailures}
                  disabled={loadingFailures}
                  data-testid="refresh-export-failures-btn"
                >
                  {loadingFailures ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loadingFailures ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : exportFailures.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No export failures recorded. 🎉
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {exportFailures.map((log) => (
                      <details
                        key={log.id}
                        className="border rounded-lg p-3 bg-red-50/40 dark:bg-red-900/10 border-red-200/60 dark:border-red-800/60"
                        data-testid={`export-failure-${log.id}`}
                      >
                        <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <Badge variant="outline" className="uppercase text-[10px] shrink-0">{log.platform}</Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">
                                {log.error_summary || 'Export failed'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                Docket {log.docket_number || log.docket_id?.slice(0, 8)}
                                {log.customer_name ? ` · ${log.customer_name}` : ''}
                                {' · '}
                                {log.attempted_at ? formatDateTime(log.attempted_at, form.date_format, form.time_format) : ''}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            HTTP {log.http_status || '—'}
                          </span>
                        </summary>
                        <div className="mt-3 pt-3 border-t border-red-200/60 dark:border-red-800/60 space-y-2 text-xs">
                          {log.error_detail && (
                            <p><span className="font-semibold">Detail:</span> {log.error_detail}</p>
                          )}
                          {log.suggested_fix && (
                            <p className="border-l-2 border-red-400 pl-2">
                              <span className="font-semibold">Suggested fix:</span> {log.suggested_fix}
                            </p>
                          )}
                          {log.attempted_by_name && (
                            <p className="text-muted-foreground">
                              Attempted by {log.attempted_by_name}
                            </p>
                          )}
                          {log.technical_response && (
                            <pre className="p-2 rounded bg-red-100 dark:bg-red-950/60 text-[11px] whitespace-pre-wrap break-all max-h-32 overflow-auto">
                              {log.technical_response}
                            </pre>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
            </TabsContent>

            <TabsContent value="payroll" className="mt-6">
              <div className="grid gap-6" data-testid="accounting-payroll">
                {!xeroStatus.connected && !myobStatus.connected && (
                  <Card className="card-industrial">
                    <CardContent className="py-8 text-center text-sm text-muted-foreground">
                      Connect Xero or MYOB in the "Connections & Invoicing" tab to configure payroll employee and rate mapping.
                    </CardContent>
                  </Card>
                )}
                {['xero', 'myob'].map((prov) => {
                  const provName = prov === 'xero' ? 'Xero' : 'MYOB';
                  const providerConnected = prov === 'xero' ? xeroStatus.connected : myobStatus.connected;
                  const conns = (acctConnections || []).filter(c => c.provider === prov && c.status !== 'disconnected');
                  // Legacy fallback: provider connected but connections not yet loaded.
                  if (conns.length === 0) {
                    if (!providerConnected) return null;
                    return (
                      <div key={prov} className="space-y-6">
                        <PayrollEmployeeMapping api={api} platform={prov} platformName={provName} />
                        <PayrollRateMapping api={api} platform={prov} platformName={provName} />
                        <TrackingRulesManager api={api} platform={prov} platformName={provName} />
                      </div>
                    );
                  }
                  if (conns.length === 1) {
                    const c = conns[0];
                    return (
                      <div key={prov} className="space-y-6">
                        <PayrollEmployeeMapping api={api} platform={prov} platformName={provName} connectionId={c.id} />
                        <PayrollRateMapping api={api} platform={prov} platformName={provName} connectionId={c.id} />
                        <TrackingRulesManager api={api} platform={prov} platformName={provName} connectionId={c.id} />
                      </div>
                    );
                  }
                  // 2+ connections of this provider → one labelled block each.
                  return conns.map((c) => {
                    const cLabel = c.label || c.company_name || provName;
                    return (
                      <div key={c.id} className="space-y-6 rounded-lg border border-border/60 p-4" data-testid={`payroll-conn-block-${c.id}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{provName}</Badge>
                          <span className="font-heading text-sm">{cLabel}{c.is_default ? ' · default' : ''}</span>
                        </div>
                        <PayrollEmployeeMapping api={api} platform={prov} platformName={`${provName} — ${cLabel}`} connectionId={c.id} />
                        <PayrollRateMapping api={api} platform={prov} platformName={`${provName} — ${cLabel}`} connectionId={c.id} />
                        <TrackingRulesManager api={api} platform={prov} platformName={`${provName} — ${cLabel}`} connectionId={c.id} />
                      </div>
                    );
                  });
                })}
              </div>
            </TabsContent>

            <TabsContent value="payroll-rules" className="mt-6">
              <PayrollRulesManager api={api} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Timesheets Settings Tab */}
        <TabsContent value="timesheets" className="mt-6">
          <div className="grid gap-6">
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Pay Cycle Settings
                </CardTitle>
                <CardDescription>
                  Configure how timesheets are grouped for payroll processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pay Cycle Type */}
                <div className="space-y-2">
                  <Label htmlFor="pay_cycle_type">Pay Cycle Frequency</Label>
                  <Select
                    value={form.pay_cycle_type}
                    onValueChange={(value) => setForm({ ...form, pay_cycle_type: value, pay_cycle_day: value === 'monthly' ? 1 : 1 })}
                  >
                    <SelectTrigger id="pay_cycle_type" className="w-full md:w-[300px]">
                      <SelectValue placeholder="Select pay cycle frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="fortnightly">Fortnightly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    How often your pay cycle runs
                  </p>
                </div>

                {/* Pay Cycle Day - Weekly/Fortnightly */}
                {(form.pay_cycle_type === 'weekly' || form.pay_cycle_type === 'fortnightly') && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_day">Pay Cycle Start Day</Label>
                    <Select
                      value={String(form.pay_cycle_day)}
                      onValueChange={(value) => setForm({ ...form, pay_cycle_day: parseInt(value) })}
                    >
                      <SelectTrigger id="pay_cycle_day" className="w-full md:w-[300px]">
                        <SelectValue placeholder="Select day of week" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                        <SelectItem value="7">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The day of the week your pay cycle begins
                    </p>
                  </div>
                )}

                {/* Pay Cycle Day - Monthly */}
                {form.pay_cycle_type === 'monthly' && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_day_monthly">Pay Cycle Start Day of Month</Label>
                    <Select
                      value={String(form.pay_cycle_day)}
                      onValueChange={(value) => setForm({ ...form, pay_cycle_day: parseInt(value) })}
                    >
                      <SelectTrigger id="pay_cycle_day_monthly" className="w-full md:w-[300px]">
                        <SelectValue placeholder="Select day of month" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={String(day)}>
                            {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The day of the month your pay cycle begins (1-28 to avoid month-end issues)
                    </p>
                  </div>
                )}

                {/* Fortnightly Reference Date */}
                {form.pay_cycle_type === 'fortnightly' && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_start_date">Fortnightly Reference Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full md:w-[300px] justify-start text-left font-normal">
                          <Calendar className="mr-2 h-4 w-4" />
                          {form.pay_cycle_start_date 
                            ? format(parse(form.pay_cycle_start_date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                            : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={form.pay_cycle_start_date ? parse(form.pay_cycle_start_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setForm({ ...form, pay_cycle_start_date: format(date, 'yyyy-MM-dd') })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      A reference date to anchor fortnightly calculations. Choose any past date when a pay cycle started.
                    </p>
                  </div>
                )}

                {/* Preview */}
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <h4 className="font-medium mb-2">Current Configuration</h4>
                  <p className="text-sm text-muted-foreground">
                    {form.pay_cycle_type === 'weekly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">weekly</span>, starting every <span className="font-medium text-foreground">{['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][form.pay_cycle_day]}</span></>
                    )}
                    {form.pay_cycle_type === 'fortnightly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">fortnightly</span>, starting every second <span className="font-medium text-foreground">{['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][form.pay_cycle_day]}</span></>
                    )}
                    {form.pay_cycle_type === 'monthly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">monthly</span>, starting on the <span className="font-medium text-foreground">{form.pay_cycle_day}{form.pay_cycle_day === 1 ? 'st' : form.pay_cycle_day === 2 ? 'nd' : form.pay_cycle_day === 3 ? 'rd' : 'th'}</span> of each month</>
                    )}
                  </p>
                </div>

                <hr className="border-muted-foreground/20" />

                {/* Payroll Calculation Method */}
                <div className="space-y-2">
                  <Label htmlFor="payroll_calc_method">Payroll Calculation Method</Label>
                  <Select
                    value={form.payroll_calc_method}
                    onValueChange={(value) => setForm({ ...form, payroll_calc_method: value })}
                  >
                    <SelectTrigger id="payroll_calc_method" className="w-full md:w-[300px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actual_hours">Actual Hours (merge overlaps)</SelectItem>
                      <SelectItem value="docket_hours">Docket Hours (sum all entries)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {form.payroll_calc_method === 'actual_hours'
                      ? 'Overlapping docket times are merged into a single time span per worker per day — prevents double-counting for payroll.'
                      : 'Each docket entry\'s hours are summed independently — useful for job costing but may overstate hours when dockets overlap.'}
                  </p>
                </div>

                <hr className="border-muted-foreground/20" />

                {/* Overtime Thresholds */}
                <div className="space-y-4">
                  <Label>Overtime Thresholds (daily hours)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="ot_threshold" className="text-xs text-muted-foreground">Ordinary hours (up to)</Label>
                      <Input
                        id="ot_threshold"
                        type="number"
                        min={1} max={24} step={0.5}
                        value={form.overtime_threshold_hours}
                        onChange={(e) => setForm({ ...form, overtime_threshold_hours: parseFloat(e.target.value) || 8 })}
                        className="w-full md:w-[200px]"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ot15_threshold" className="text-xs text-muted-foreground">Overtime 1.5x (up to)</Label>
                      <Input
                        id="ot15_threshold"
                        type="number"
                        min={1} max={24} step={0.5}
                        value={form.overtime_1_5_threshold_hours}
                        onChange={(e) => setForm({ ...form, overtime_1_5_threshold_hours: parseFloat(e.target.value) || 10 })}
                        className="w-full md:w-[200px]"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Hours above {form.overtime_1_5_threshold_hours}h/day are calculated at double time (2.0x). 
                    Example: {form.overtime_threshold_hours}h ordinary + {(form.overtime_1_5_threshold_hours - form.overtime_threshold_hours).toFixed(1)}h at 1.5x + remainder at 2.0x
                  </p>
                </div>

                <Button 
                  onClick={handleSavePayCycle} 
                  disabled={savingPayCycle}
                  className="btn-volt"
                >
                  {savingPayCycle ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Pay Cycle Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Public Holidays */}
            <PublicHolidaysManager api={api} />
          </div>
        </TabsContent>

        {/* My Account Tab */}
        <TabsContent value="account" className="mt-6">
          <div className="space-y-6">
            {/* Account Info */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Account Information
                </CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Full Name</p>
                    <p className="font-medium">{user?.full_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Username</p>
                    <p className="font-mono">{user?.username}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Role</p>
                    {getRoleBadges(user)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Client ID</p>
                    <p className="font-mono">{user?.client_id}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <KeyRound className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label className="label-overline">Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Enter current password"
                        required
                        data-testid="current-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password"
                        required
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Confirm New Password</Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Password Requirements:</p>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordForm.newPassword.length >= 8 ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={passwordForm.newPassword.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {/\d/.test(passwordForm.newPassword) ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={/\d/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                        Contains at least one number
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordForm.newPassword && passwordForm.newPassword === passwordForm.confirmPassword ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={passwordForm.newPassword && passwordForm.newPassword === passwordForm.confirmPassword ? 'text-green-600' : 'text-muted-foreground'}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Button 
                      type="submit" 
                      className="btn-volt"
                      disabled={passwordLoading}
                      data-testid="change-password-btn"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        <>
                          <KeyRound className="h-4 w-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPasswords(!showPasswords)}
                    >
                      {showPasswords ? (
                        <><EyeOff className="h-4 w-4 mr-2" /> Hide</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-2" /> Show</>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="mt-6">
          <SupportTab />
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="mt-6">
          <form onSubmit={handleSubmit}>
            {/* Job Automation Rules */}
            <Card className="card-industrial mb-6">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Automation Rules
                </CardTitle>
                <CardDescription>Control automatic job status transitions and workflow behaviour</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Auto Confirm Jobs */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Auto-Confirm Jobs on Allocation</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      When enabled, jobs are automatically set to <strong>Confirmed</strong> status when an asset and operator are allocated. When disabled, jobs remain at <strong>Allocated</strong> status and must be manually confirmed.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.auto_confirm_jobs ?? true}
                      onCheckedChange={(checked) => {
                        setForm({ ...form, auto_confirm_jobs: checked });
                      }}
                      data-testid="rules-auto-confirm-toggle"
                    />
                  </div>
                </div>

                {/* Relaxed Allocation Mode */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Relaxed Allocation Mode</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      When enabled, the planning board <strong>skips</strong> the drag-and-drop capability checks — worker operates the asset type, asset matches the asset type, worker type match and double-booking — allowing any allocation without a warning. <strong>Compliance and dry-hire locks are always enforced.</strong>
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.relaxed_allocation ?? false}
                      onCheckedChange={async (checked) => {
                        setForm({ ...form, relaxed_allocation: checked });
                        try {
                          await api.put(`/tenants/${user.client_id}`, { ...form, relaxed_allocation: checked });
                          toast.success(checked ? 'Relaxed allocation mode enabled' : 'Relaxed allocation mode disabled');
                        } catch (error) {
                          toast.error('Failed to update setting');
                          setForm({ ...form, relaxed_allocation: !checked });
                        }
                      }}
                      data-testid="rules-relaxed-allocation-toggle"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  Field Rules
                </CardTitle>
                <CardDescription>Control which optional fields are visible and required across the application</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Job Fields</h3>
                
                {/* Depot Time */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Depot Time</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Track when operators leave the depot before arriving on site. Shows on Schedule, Planning Board, and Mobile Portal.
                    </p>
                    {form.field_rules?.depot_time?.enabled && (
                      <div className="mt-3 ml-6 flex items-center gap-4">
                        <div className="space-y-1">
                          <Label className="label-overline text-[10px]">Default Depot Time</Label>
                          <TimeField
                            value={form.default_depot_time}
                            onChange={(v) => setForm({ ...form, default_depot_time: v })}
                            className="w-32"
                            data-testid="rules-depot-time-default"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.depot_time?.enabled || false}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, depot_time: { ...form.field_rules?.depot_time, enabled: checked } };
                        setForm({ ...form, field_rules: newRules, depot_time_enabled: checked });
                      }}
                      data-testid="rules-depot-time-toggle"
                    />
                  </div>
                </div>
                {/* Job & Docket Numbering */}
                <div className="p-4 border border-border rounded-lg" data-testid="rules-job-numbering-card">
                  <div className="flex items-start gap-2">
                    <Hash className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">Job &amp; Docket Numbering</span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Set the next job number. Because a docket takes its number from its job, this controls both. Existing records are never changed.
                      </p>
                      <div className="mt-3 flex flex-wrap items-end gap-4">
                        <div className="space-y-1">
                          <Label className="label-overline text-[10px]">Next number</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={jobNumbering?.min_allowed || 1}
                              value={numberingInput}
                              onChange={(e) => setNumberingInput(e.target.value)}
                              className="w-40 font-mono text-sm"
                              placeholder="e.g. 2501"
                              data-testid="rules-next-number-input"
                            />
                            <Button
                              size="sm"
                              onClick={handleSaveJobNumbering}
                              disabled={savingNumbering || !jobNumbering || String(numberingInput) === String(jobNumbering?.next_number)}
                              data-testid="rules-next-number-save-btn"
                            >
                              {savingNumbering ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        </div>
                        {jobNumbering && (
                          <p className="text-xs text-muted-foreground pb-1.5" data-testid="rules-next-number-hint">
                            The next job &amp; docket will be <span className="font-mono font-semibold text-foreground">#{jobNumbering.next_number}</span>.
                            {jobNumbering.highest_used > 0 && <> Minimum you can set: <span className="font-mono">{jobNumbering.min_allowed}</span> (highest used: {jobNumbering.highest_used}).</>}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Worker job notifications */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Notify workers of job details</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When on, dispatchers get a "Notify Workers" action on jobs to send job details (job #, date, start time, site, equipment) to allocated workers via each worker's preferred delivery method.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={form.worker_job_email_enabled || false}
                        onCheckedChange={(checked) => setForm({ ...form, worker_job_email_enabled: checked })}
                        data-testid="rules-worker-email-toggle"
                      />
                    </div>
                  </div>
                  {form.worker_job_email_enabled && (
                    <div className="ml-6 flex items-center justify-between gap-4">
                      <div>
                        <span className="text-sm font-medium">Default delivery method</span>
                        <p className="text-xs text-muted-foreground mt-0.5">Used for workers who don't have a preference set on their profile.</p>
                      </div>
                      <Select
                        value={form.default_worker_notification_preference || 'email'}
                        defaultValue="email"
                        onValueChange={(v) => setForm({ ...form, default_worker_notification_preference: v })}
                      >
                        <SelectTrigger className="w-40" data-testid="rules-worker-default-pref">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="push">Push</SelectItem>
                          <SelectItem value="both">Email + Push</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {/* Enable Depots */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Depots</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Enable depot locations. When on, you can manage depots in Data Management, assign assets to a depot, and select a depot when creating a job.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.depots_enabled || false}
                      onCheckedChange={(checked) => setForm({ ...form, depots_enabled: checked })}
                      data-testid="rules-depots-toggle"
                    />
                  </div>
                </div>
                {/* Additional Workers */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Additional Workers</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Allow adding additional worker requirements (e.g. Dogmen, Spotters, Riggers) when creating or editing a job. When disabled, the Additional Workers section is hidden from the job form.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.additional_workers?.enabled ?? true}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, additional_workers: { ...form.field_rules?.additional_workers, enabled: checked } };
                        setForm({ ...form, field_rules: newRules });
                      }}
                      data-testid="rules-additional-workers-toggle"
                    />
                  </div>
                </div>
                {/* Asset Allocation on Create */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Asset Allocation on Create</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      When creating a new job, allow selecting a specific asset (equipment) to allocate after choosing an Asset Type. The asset will be automatically allocated to the job upon creation.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.asset_allocation_on_create?.enabled || false}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, asset_allocation_on_create: { ...form.field_rules?.asset_allocation_on_create, enabled: checked } };
                        setForm({ ...form, field_rules: newRules });
                      }}
                      data-testid="rules-asset-allocation-on-create-toggle"
                    />
                  </div>
                </div>
                {/* SMU (Service Meter Unit) Tracking */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">SMU (Service Meter Unit) Tracking</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        Workers capture each asset's meter reading at the start and finish of each job. Invoice lines can be calculated from the meter delta instead of the time worked.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={form.field_rules?.smu?.enabled || false}
                        onCheckedChange={(checked) => {
                          const newRules = {
                            ...form.field_rules,
                            smu: { ...form.field_rules?.smu, enabled: checked },
                          };
                          setForm({ ...form, field_rules: newRules });
                        }}
                        data-testid="rules-smu-toggle"
                      />
                    </div>
                  </div>
                  {form.field_rules?.smu?.enabled && (
                    <div className="mt-4 ml-6 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Label className="text-sm">Capture secondary meter</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Show a 2nd reading on mobile (e.g. KMs alongside engine hours on trucks).
                          </p>
                        </div>
                        <Switch
                          checked={form.field_rules?.smu?.secondary_meter_enabled || false}
                          onCheckedChange={(checked) => {
                            const newRules = {
                              ...form.field_rules,
                              smu: { ...form.field_rules?.smu, secondary_meter_enabled: checked },
                            };
                            setForm({ ...form, field_rules: newRules });
                          }}
                          data-testid="rules-smu-secondary-toggle"
                        />
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Label className="text-sm">Required to complete job</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Block docket submission on mobile if meter readings are missing.
                          </p>
                        </div>
                        <Switch
                          checked={form.field_rules?.smu?.required_to_complete_job || false}
                          onCheckedChange={(checked) => {
                            const newRules = {
                              ...form.field_rules,
                              smu: { ...form.field_rules?.smu, required_to_complete_job: checked },
                            };
                            setForm({ ...form, field_rules: newRules });
                          }}
                          data-testid="rules-smu-required-toggle"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Invoice calculation mode</Label>
                        <Select
                          value={form.field_rules?.smu?.invoice_calculation_mode || 'prefer_smu_fall_back_to_time'}
                          onValueChange={(v) => {
                            const newRules = {
                              ...form.field_rules,
                              smu: { ...form.field_rules?.smu, invoice_calculation_mode: v },
                            };
                            setForm({ ...form, field_rules: newRules });
                          }}
                        >
                          <SelectTrigger className="mt-1 w-full max-w-md" data-testid="rules-smu-mode-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="time_only">Time only (ignore meter readings)</SelectItem>
                            <SelectItem value="prefer_smu_fall_back_to_time">Prefer SMU, fall back to time (recommended)</SelectItem>
                            <SelectItem value="smu_only">SMU only (strict)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                {/* Allow Linked Jobs */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Allow Linked Jobs</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Enable the ability to create sub-jobs linked to a parent job. Sub-jobs inherit the parent's customer, site, dates, and asset type. Job numbers display as Parent#-Index (e.g. 57-1, 57-2).
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.allow_linked_jobs?.enabled || false}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, allow_linked_jobs: { ...form.field_rules?.allow_linked_jobs, enabled: checked } };
                        setForm({ ...form, field_rules: newRules });
                      }}
                      data-testid="rules-allow-linked-jobs-toggle"
                    />
                  </div>
                </div>
                {/* Allow Multi-Stop Jobs */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Allow Multi-Stop Jobs</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Enable the ability to add Pickup, Dropoff, and Waypoint stops to any job type. When toggled on, a "Requires Multi-Stops" option appears on the job creation form.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.allow_multi_stops?.enabled || false}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, allow_multi_stops: { ...form.field_rules?.allow_multi_stops, enabled: checked } };
                        setForm({ ...form, field_rules: newRules });
                      }}
                      data-testid="rules-allow-multi-stops-toggle"
                    />
                  </div>
                </div>
                {/* Require Estimated Duration */}
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Require Estimated Duration</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Make the estimated duration field mandatory when creating or editing a job. Improves the accuracy of revenue forecasting by ensuring every job has an hourly estimate.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Switch
                      checked={form.field_rules?.require_est_duration?.enabled || false}
                      onCheckedChange={(checked) => {
                        const newRules = { ...form.field_rules, require_est_duration: { ...form.field_rules?.require_est_duration, enabled: checked } };
                        setForm({ ...form, field_rules: newRules });
                      }}
                      data-testid="rules-require-est-duration-toggle"
                    />
                  </div>
                </div>
                {/* Starting Location */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Navigation className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Starting Location</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        Show a Starting Location field on the Create/Edit Job form to record the address workers start from (Google address lookup). Visible on the Mobile app for workers and dispatchers.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={form.field_rules?.starting_location?.enabled || false}
                        onCheckedChange={(checked) => {
                          const newRules = { ...form.field_rules, starting_location: { ...form.field_rules?.starting_location, enabled: checked } };
                          setForm({ ...form, field_rules: newRules });
                        }}
                        data-testid="rules-starting-location-toggle"
                      />
                    </div>
                  </div>
                  {form.field_rules?.starting_location?.enabled && (
                    <div className="mt-4 ml-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Label className="text-sm">Make mandatory</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Require a Starting Location before a job can be saved.
                          </p>
                        </div>
                        <Switch
                          checked={form.field_rules?.starting_location?.required || false}
                          onCheckedChange={(checked) => {
                            const newRules = { ...form.field_rules, starting_location: { ...form.field_rules?.starting_location, required: checked } };
                            setForm({ ...form, field_rules: newRules });
                          }}
                          data-testid="rules-starting-location-required-toggle"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Display Preferences */}
            <Card className="card-industrial mt-4">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Display Preferences
                </CardTitle>
                <CardDescription>Configure how times are displayed across the application</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Time Display Format</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      Choose between 24-hour (14:00) or 12-hour (2:00 PM) time format. Applies site-wide including Schedule, Mobile Portal, Timesheets, and Chat.
                    </p>
                    <div className="mt-3 ml-6">
                      <Select
                        value={form.time_format}
                        onValueChange={(value) => setForm({ ...form, time_format: value })}
                      >
                        <SelectTrigger className="w-64" data-testid="rules-time-format-select">
                          <SelectValue placeholder="Select time format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24h">24-hour (14:00)</SelectItem>
                          <SelectItem value="12h">12-hour (2:00 PM)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end mt-6">
              <Button type="submit" className="btn-volt" data-testid="save-rules-btn">
                <Save className="h-4 w-4 mr-2" />
                Save Rules
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Dockets Tab — consolidated docket Rules + PDF Template */}
        <TabsContent value="docket-pdf" className="mt-6">
          <Tabs defaultValue="docket-rules" className="w-full">
            <TabsList data-testid="dockets-subtabs">
              <TabsTrigger value="docket-rules" data-testid="dockets-subtab-rules">Rules</TabsTrigger>
              <TabsTrigger value="docket-template" data-testid="dockets-subtab-template">PDF Template</TabsTrigger>
            </TabsList>
            <TabsContent value="docket-rules" className="mt-6">
              <form onSubmit={handleSubmit}>
            {/* Invoice Rules */}
            <Card className="card-industrial mt-4">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Invoice
                </CardTitle>
                <CardDescription>Configure how invoice line items are automatically calculated from docket data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Overtime */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Overtime Calculation</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        Automatically generate overtime line items on dockets when hours exceed normal thresholds. Uses overtime rates from your price lists.
                      </p>
                    </div>
                  </div>

                  <div className="ml-6 space-y-3">
                    <div>
                      <Label className="label-overline text-[10px]">Calculation Method</Label>
                      <Select
                        value={form.overtime_method}
                        onValueChange={(value) => setForm({ ...form, overtime_method: value })}
                      >
                        <SelectTrigger className="w-80 mt-1" data-testid="rules-overtime-method-select">
                          <SelectValue placeholder="Select overtime method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disabled">Disabled</SelectItem>
                          <SelectItem value="threshold">Method 1 — Hours over threshold</SelectItem>
                          <SelectItem value="outside_normal_hours">Method 2 — Outside normal hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.overtime_method === 'threshold' && (
                      <div>
                        <Label className="label-overline text-[10px]">Threshold (hours)</Label>
                        <p className="text-xs text-muted-foreground mb-1">Any time worked beyond this many hours is overtime</p>
                        <Input
                          type="number"
                          step="0.5"
                          min="1"
                          max="24"
                          value={form.overtime_threshold_hours}
                          onChange={(e) => setForm({ ...form, overtime_threshold_hours: parseFloat(e.target.value) || 8 })}
                          className="w-32 font-mono"
                          data-testid="rules-overtime-threshold-input"
                        />
                      </div>
                    )}

                    {form.overtime_method === 'outside_normal_hours' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          Uses your normal working hours from Organisation settings ({form.default_working_hours?.start || '07:00'} – {form.default_working_hours?.end || '17:00'}). Any time worked before or after this window is calculated as overtime.
                        </p>
                      </div>
                    )}

                    {form.overtime_method !== 'disabled' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Overtime line items will be generated automatically when docket prices are calculated. Ensure your price lists have overtime rates configured for each equipment and worker type.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Travel */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Travel Charges</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        Automatically generate travel line items on dockets. When enabled, travel time is excluded from the equipment/worker total hours and charged separately using travel rates from your price lists.
                      </p>
                    </div>
                  </div>

                  <div className="ml-6 space-y-3">
                    <div>
                      <Label className="label-overline text-[10px]">Calculation Method</Label>
                      <Select
                        value={form.travel_method}
                        onValueChange={(value) => setForm({ ...form, travel_method: value })}
                      >
                        <SelectTrigger className="w-80 mt-1" data-testid="rules-travel-method-select">
                          <SelectValue placeholder="Select travel method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disabled">Disabled</SelectItem>
                          <SelectItem value="flat_rate">Flat Rate (each way)</SelectItem>
                          <SelectItem value="hourly">Hourly (based on travel time)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.travel_method === 'flat_rate' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          Each docket line item with travel time will be charged a flat travel rate for each way (default: 2 trips — to and from site). The travel rate is sourced from your price lists.
                        </p>
                      </div>
                    )}

                    {form.travel_method === 'hourly' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          Travel time recorded on each docket line item will be charged at the hourly travel rate from your price lists. Travel time will be excluded from the item's total hours.
                        </p>
                      </div>
                    )}

                    {form.travel_method === 'hourly' && (
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <div className="flex-1">
                          <span className="font-medium text-sm">Travel time counts towards minimum hire</span>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            When on, hourly travel is included when checking a line's minimum hire, so the customer pays the minimum in total rather than the minimum plus travel. Example: 2h on site + 1h travel with a 4h minimum bills 3h + 1h travel, not 4h + 1h.
                          </p>
                        </div>
                        <Switch
                          checked={form.min_hire_includes_travel ?? false}
                          onCheckedChange={(checked) => setForm({ ...form, min_hire_includes_travel: checked })}
                          data-testid="rules-min-hire-includes-travel"
                        />
                      </div>
                    )}

                    {form.travel_method !== 'disabled' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Travel line items will be generated automatically and travel time will be excluded from equipment/worker hours. Ensure your price lists have travel rates configured.
                        </p>
                      </div>
                    )}

                    {isFlagEnabled('docket_depot_times') && (
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <div className="flex-1">
                          <span className="font-medium text-sm">Record depot departure/arrival times</span>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Workers enter Depart Depot and Arrive Depot times. Start/Finish become Arrive Site/Depart Site, and travel is calculated from the times.
                          </p>
                        </div>
                        <Switch
                          checked={form.docket_depot_times_enabled ?? false}
                          onCheckedChange={(checked) => setForm({ ...form, docket_depot_times_enabled: checked })}
                          data-testid="docket-depot-times-toggle"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Minimum Charge */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Minimum Charge</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When enabled, if an equipment or worker line item's hours are below the Min Hire value set in the price list, the quantity is automatically bumped up to meet the minimum.
                      </p>
                    </div>
                    <Switch
                      checked={form.minimum_charge_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, minimum_charge_enabled: checked })}
                      data-testid="rules-minimum-charge-toggle"
                    />
                  </div>

                  {form.minimum_charge_enabled && (
                    <div className="ml-6">
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          The Min Hire value is configured per equipment/worker type in your price lists. If a line item's hours (after travel exclusion) fall below this value, the quantity will be increased to match it and a note will be added to the line item description.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Separate Primary Operator Line */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Separate Primary Operator Line</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When enabled, docket creation automatically adds a separate line item for the primary operator so their hours can be tracked and invoiced independently of the equipment hours.
                      </p>
                    </div>
                    <Switch
                      checked={form.separate_operator_line ?? false}
                      onCheckedChange={(checked) => setForm({ ...form, separate_operator_line: checked })}
                      data-testid="separate-operator-line-toggle"
                    />
                  </div>

                  {form.separate_operator_line && (
                    <div className="ml-6">
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          The operator will appear as a worker line item using their assigned worker type. Ensure your price lists include a rate for the operator's worker type. The operator's start/finish times will default to the equipment times and can be adjusted independently.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Charge Fuel Levy */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Charge Fuel Levy</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When enabled, a "Fuel Levy" line is auto-calculated and added to invoices. It recalculates whenever invoice lines change.
                      </p>
                    </div>
                    <Switch
                      checked={form.fuel_levy_enabled ?? false}
                      onCheckedChange={(checked) => setForm({ ...form, fuel_levy_enabled: checked })}
                      data-testid="fuel-levy-toggle"
                    />
                  </div>

                  {form.fuel_levy_enabled && (
                    <div className="ml-6 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm">Percentage (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={form.fuel_levy_percentage}
                            onChange={(e) => setForm({ ...form, fuel_levy_percentage: parseFloat(e.target.value) || 0 })}
                            placeholder="e.g. 10"
                            data-testid="fuel-levy-percentage-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Calculate On</Label>
                          <Select
                            value={form.fuel_levy_basis}
                            onValueChange={(value) => setForm({ ...form, fuel_levy_basis: value })}
                          >
                            <SelectTrigger data-testid="fuel-levy-basis-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="invoice_total">Invoice Total (ex tax)</SelectItem>
                              <SelectItem value="asset_only">Asset Only (equipment lines)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Account ID (optional)</Label>
                          <Input
                            value={form.fuel_levy_account_id}
                            onChange={(e) => setForm({ ...form, fuel_levy_account_id: e.target.value })}
                            placeholder="e.g. 200"
                            data-testid="fuel-levy-account-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {form.fuel_levy_basis === 'asset_only'
                          ? 'Fuel levy is calculated on equipment (asset) line totals only, ex tax.'
                          : 'Fuel levy is calculated on the full invoice total, ex tax.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Instant Confirm (Skip Approval Step) */}
                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Instant Confirm (Skip Approval Step)</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When enabled, a draft docket can be confirmed in one click — going straight to Approved without a separate "Submit for Approval" step. Confirming still requires approve permission and runs all invoice calculations.
                      </p>
                    </div>
                    <Switch
                      checked={form.skip_docket_approval ?? false}
                      onCheckedChange={(checked) => setForm({ ...form, skip_docket_approval: checked })}
                      data-testid="skip-docket-approval-toggle"
                    />
                  </div>
                </div>

                {/* Remote Signature Link Expiry */}
                <div className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <PenLine className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Signature Request Link Expiry</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    When you request a remote signature on a docket that was submitted "unable to obtain signature", the secure signing link stays valid for this many hours.
                  </p>
                  <div className="flex items-center gap-2 ml-6">
                    <Input
                      type="number"
                      min={1}
                      max={720}
                      value={form.docket_signature_link_expiry_hours ?? 24}
                      onChange={(e) => setForm({ ...form, docket_signature_link_expiry_hours: parseInt(e.target.value) || 0 })}
                      className="w-28"
                      data-testid="signature-link-expiry-input"
                    />
                    <span className="text-sm text-muted-foreground">hours</span>
                  </div>
                </div>
              </CardContent>
            </Card>
              <Card className="card-industrial mt-4">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Docket PDF
                  </CardTitle>
                  <CardDescription>Configure what's included in generated docket PDFs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Include Photos in PDF</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When enabled, operator photos will be appended as pages to the docket PDF sent to customers
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={form.include_photos_in_docket_pdf ?? false}
                        onCheckedChange={(checked) => setForm({ ...form, include_photos_in_docket_pdf: checked })}
                        data-testid="include-photos-in-pdf-toggle"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Auto-Email Docket on Mobile Submit</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        When a driver submits a docket from the mobile app, automatically email a PDF copy to the customer's <span className="font-medium">Docket To Email</span> and any site contacts selected on the job. Customers can be opted out individually.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Switch
                        checked={form.auto_email_docket_on_submit ?? false}
                        onCheckedChange={(checked) => setForm({ ...form, auto_email_docket_on_submit: checked })}
                        data-testid="auto-email-docket-toggle"
                      />
                    </div>
                  </div>
                  {/* Recipient group checkboxes — visible when auto-email is on */}
                  {form.auto_email_docket_on_submit && (
                    <div className="ml-6 mt-3 p-3 border border-border rounded-lg bg-muted/30 space-y-2" data-testid="auto-email-recipient-groups">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Who receives the auto-email?</p>
                      {[
                        { key: 'customer', label: "Customer's Docket-To Email", desc: 'The dedicated docket email address set on the customer record' },
                        { key: 'site_contacts', label: 'Site Contacts on the Job', desc: 'Any site contacts selected on the job that have an email' },
                        { key: 'creator', label: 'Docket Creator / Operator', desc: 'The driver or admin who submitted the docket' },
                      ].map(g => {
                        const groups = form.auto_email_recipient_groups || [];
                        const checked = groups.includes(g.key);
                        return (
                          <label key={g.key} className="flex items-start gap-2 cursor-pointer" data-testid={`auto-email-group-${g.key}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                const next = v ? [...groups, g.key] : groups.filter(k => k !== g.key);
                                setForm({ ...form, auto_email_recipient_groups: next });
                              }}
                              className="mt-0.5"
                            />
                            <div>
                              <span className="text-sm font-medium">{g.label}</span>
                              <p className="text-xs text-muted-foreground">{g.desc}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Haulage Docket Signatures */}
              <Card className="card-industrial mt-4">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Haulage Docket Signatures
                  </CardTitle>
                  <CardDescription>Control when and how the customer signs haulage dockets. These are defaults — individual jobs can override them.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 border border-border rounded-lg">
                    <div className="font-medium text-sm mb-1">When is the customer signature captured?</div>
                    <p className="text-xs text-muted-foreground mb-3">Choose whether the customer signs at the start of the day (authorisation), at the end of day (confirmation), or both.</p>
                    <div className="grid grid-cols-4 gap-2" data-testid="haulage-signature-timing">
                      {[{ v: 'end', l: 'End of day' }, { v: 'start', l: 'Start of day' }, { v: 'both', l: 'Both' }, { v: 'anytime', l: 'Anytime' }].map((opt) => (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setForm({ ...form, haulage_signature_timing: opt.v })}
                          className={`px-3 py-2 rounded-md text-sm border transition-colors ${form.haulage_signature_timing === opt.v ? 'bg-[#ffd108] text-black border-[#ffd108] font-medium' : 'bg-transparent text-muted-foreground border-border hover:border-[#ffd108]/50'}`}
                          data-testid={`haulage-signature-timing-${opt.v}`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex-1">
                      <span className="font-medium text-sm">Capture a signature at every waypoint</span>
                      <p className="text-xs text-muted-foreground mt-1">When off, drivers sign the docket as a whole instead of at each stop. Photos & notes are still captured per stop.</p>
                    </div>
                    <Switch
                      checked={form.haulage_waypoint_signature ?? true}
                      onCheckedChange={(checked) => setForm({ ...form, haulage_waypoint_signature: checked })}
                      data-testid="haulage-waypoint-signature-toggle"
                    />
                  </div>
                </CardContent>
              </Card>

                <div className="flex justify-end mt-6">
                  <Button type="submit" className="btn-volt" data-testid="save-docket-rules-btn">
                    <Save className="h-4 w-4 mr-2" />
                    Save Rules
                  </Button>
                </div>
              </form>
              <div className="mt-6">
                <DocketLineRulesSettings />
              </div>
            </TabsContent>
            <TabsContent value="docket-template" className="mt-6">
              <DocketPdfTemplateSettings />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="space-y-6">
            <NotificationsSettingsTab />
          </div>
        </TabsContent>
        {isDemoTenant && (
          <TabsContent value="integrations" className="mt-6">
            <AWPIntegrationSettings api={api} />
          </TabsContent>
        )}
      </Tabs>

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={<span style={{ whiteSpace: 'pre-wrap' }}>{confirmDialog.description}</span>}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Confirm"
        variant="info"
      />
    </div>
  );
};

export default Settings;
